import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { api, aiApi, extractApiError } from '../api/client';
import { SkeletonMetricCard, SkeletonChart } from '../components/SkeletonLoader';
import { formatCurrency, timeAgo } from '../utils/format';
import type { DashboardData } from '@shared/types';
import type { Pipeline } from '../store/pipelineStore';
import { StartupBriefCard } from '../components/dashboard/StartupBriefCard';
import { StageMetrics } from '../components/dashboard/StageMetrics';
import { NextMoveCard } from '../components/dashboard/NextMoveCard';
import { StageLifecycleBar } from '../components/dashboard/StageLifecycleBar';
import { SynthesisCard } from '../components/dashboard/SynthesisCard';
import type { IntelSectionsForSynthesis } from '../components/dashboard/SynthesisCard';
import { FounderSummaryEditor } from '../components/dashboard/FounderSummaryEditor';
import { BetaRecruitmentPanel } from '../components/dashboard/BetaRecruitmentPanel';
import { useReadinessStore } from '../store/readinessStore';
import type { ReadinessItem } from '../store/readinessStore';
import { useCreditsStore, getCost } from '../store/creditsStore';
import InsufficientCreditsModal from '../components/InsufficientCreditsModal';
import DailyMissionCard from '../components/dashboard/DailyMissionCard';
import ValidationTracker from '../components/dashboard/ValidationTracker';
import SignalLog from '../components/dashboard/SignalLog';
import MVPBlueprint from '../components/dashboard/MVPBlueprint';
import MilestoneCelebration, { MILESTONES } from '../components/dashboard/MilestoneCelebration';
import ShareProgressButton from '../components/dashboard/ShareProgressButton';
import DeepAnalysis from '../components/dashboard/DeepAnalysis';
import FeatureGate from '../components/FeatureGate';
import { useSubscription } from '../hooks/useSubscription';
import { sanitizeCustomerVoice, sanitizeDesire, sanitizeLabel, isPromptArtifact } from '../utils/sanitizeAI';
import {
  BarChart2, FlaskConical, Hammer, BrainCircuit, ShieldCheck, Key, Swords,
  TrendingUp, MessageSquare, Users, LineChart as LineChartIcon, UserCheck,
  BookOpen, Sparkles, ClipboardList, Activity, Zap, Lightbulb, Copy, Play,
  FileDown, BellOff, Share2, ChevronDown, ArrowLeft, Check,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────
type SectionStatus = 'idle' | 'loading' | 'done' | 'error';
interface IntelSection<T> { status: SectionStatus; data: T | null; error: string; analyzedAt: string | null; }

interface NameValidationData {
  name: string;
  brandScore: { overall: number; availability: number; memorability: number; marketFit: number; growthPotential: number };
  nameStory: { verdict: 'strong' | 'promising' | 'risky' | 'rebrand'; headline: string; strategicAnalysis: string; phoneticsNote: string; emotionalResponse: string };
  brandPersonality: { archetype: string; traits: string[]; toneOfVoice: string; colorPalette: string[] };
  messagingFramework: { taglines: string[]; elevatorPitch: string; positioningStatement: string };
  domainStrategy: { primary: string; alternatives: string[]; verdict: string; estimatedCost: string };
  legalAnalysis: { trademarkRisk: 'low' | 'medium' | 'high'; conflicts: string[]; recommendation: string; jurisdictions: string[] };
  marketPresence: { directConflicts: Array<{ name: string; url: string; threat: 'low' | 'medium' | 'high'; note: string }>; similarNames: string[]; differentiationScore: number };
  brandAlternatives: Array<{ name: string; reasoning: string; style: string; availableDomain: string; score: number }>;
  actionPlan: { thisWeek: string[]; thisMonth: string[]; beforeLaunch: string[] };
  seoAnalysis: { searchVolume: string; competitiveness: string; keywordSynergy: string; recommendation: string };
  trademarkMentions: Array<{ title: string; url: string }>;
  recentNews: Array<{ title: string; url: string; age: string }>;
  // Legacy fields for backward compat
  riskScore?: number; riskLevel?: 'low' | 'medium' | 'high';
  nameStrengthScore?: number; nameStrengthFactors?: Array<{ factor: string; positive: boolean }>;
  legalRiskAssessment?: { level: string; reasons: string[]; recommendation: string; urgentAction: string | null };
}

type IntentType = 'informational' | 'navigational' | 'commercial' | 'transactional';
interface KwItem {
  term: string; volumeEstimate: 'high' | 'medium' | 'low'; volumeNumber: number;
  difficulty: 'hard' | 'medium' | 'easy'; category: 'primary' | 'secondary' | 'long_tail';
  intent: IntentType; contentOpportunityScore: number; suggestedTitle: string;
  estimatedMonthlyClicks: number; why: string; actionableInsight: string;
}
interface KeywordsData {
  keywords: KwItem[]; seedKeywords: string[]; quickWins: KwItem[];
  contentCalendar: Array<{ week: number; topic: string; targetKeyword: string; contentType: string; why: string }>;
  seoStrategy: { phase1: string; phase2: string; phase3: string; estimatedTimeToRank: string };
  totalOpportunity: { estimatedMonthlyTraffic: number; estimatedMonthlyLeads: number; conversionAssumption: string };
}

interface CompetitorItemFull {
  name: string; domain: string; description: string; threatLevel: 'high' | 'medium' | 'low';
  pricing: { model: string; detectedPrice: string | null };
  targetCustomer: string; detectedStrengths: string[]; detectedWeaknesses: string[];
  recentNews: string | null; howToBeat: string; fundingDetected: string | null;
  geography: string | null; estimatedAge: string | null; weaknessCategories: string[];
}
interface CompetitorsData {
  competitors: CompetitorItemFull[];
  marketOverview: {
    crowdingScore: number; crowdingLabel: 'uncrowded' | 'moderate' | 'crowded' | 'saturated';
    dominantModel: string;
    priceRange: { min: string | null; max: string | null; median: string | null };
    fundingActivity: 'none' | 'low' | 'moderate' | 'high'; fundedCount: number;
  };
  competitorWeaknessMap: { pricing: number; complexity: number; support: number; features: number; performance: number };
  battleCards: Array<{
    competitor: string; theyWinWhen: string; youWinWhen: string; keyDifferentiator: string;
    salesScript: string; objection: string; objectionResponse: string;
  }>;
  positioningRecommendation: {
    statement: string;
    taglines: Array<{ text: string; angle: string }>;
    avoid: string;
  };
  fundingLandscape: {
    recentRounds: Array<{ company: string; amount: string; stage: string; source: string }>;
    investorInterestBarometer: 'cold' | 'warm' | 'hot';
    signals: string[];
  };
  geographicOpportunities: string[];
  strategicAdvice: Array<{ priority: 'p0' | 'p1' | 'p2'; action: string; rationale: string; timeframe: string }>;
  marketGaps: Array<{ gap: string; opportunity: string; complaintCount: number }>;
}

interface TrendsData {
  marketSize: { value: string | null; source: string | null; year: string | null };
  growthRate: { value: string | null; source: string | null };
  trendingTopics: Array<{ topic: string; momentum: 'rising' | 'stable' | 'declining'; description: string; source: string }>;
  industryNews: Array<{ title: string; summary: string; url: string; source: string; date: string; sentiment: 'positive' | 'negative' | 'neutral' }>;
  regulatoryRisks: Array<{ risk: string; description: string; urgency: string }>;
  investorInterest: { level: 'high' | 'medium' | 'low'; signals: string[] };
  timingAssessment: { score: number; verdict: string; reasoning: string };
  founderOpportunities: Array<{ opportunity: string; why: string; actionableStep: string }>;
  threats: Array<{ threat: string; severity: 'high' | 'medium' | 'low'; mitigation: string }>;
}

interface SentimentData {
  overallSentiment: string; sentimentScore: number; positiveCount: number; negativeCount: number;
  realQuotes: Array<{ text: string; source: string; sentiment: 'positive' | 'negative'; emotion: string; insightType: string }>;
  painPoints: Array<{ point: string; frequency: number; severity: 'critical' | 'major' | 'minor'; exampleQuote: string; productImplication: string }>;
  desires: Array<{ desire: string; frequency: number; exampleQuote: string; featureIdea: string }>;
  emotionalTriggers: string[];
  customerVoice: { topFrustration: string; topDesire: string; dreamOutcome: string };
  copywritingGold: Array<{ type: 'headline' | 'value_prop' | 'cta'; text: string; basedOn: string }>;
  productRoadmapHints: Array<{ feature: string; priority: 'p0' | 'p1' | 'p2'; basedOnSentiment: string }>;
  messagingFramework: { openWithPain: string; agitate: string; solution: string; cta: string };
}

interface IntelState {
  nameValidation: IntelSection<NameValidationData>;
  keywords: IntelSection<KeywordsData>;
  competitors: IntelSection<CompetitorsData>;
  trends: IntelSection<TrendsData>;
  sentiment: IntelSection<SentimentData>;
}

const initSection = <T,>(): IntelSection<T> => ({ status: 'idle', data: null, error: '', analyzedAt: null });

// ── Sub-components ────────────────────────────────────────────────
function Sk({ w = '100%', h = 20 }: { w?: string | number; h?: number }) {
  return <div style={{ width: w, height: h, borderRadius: 6, background: '#1e1e30', animation: 'wizard-blob 2s ease-in-out infinite' }} />;
}

function BrandScoreRing({ score }: { score: number }) {
  const color = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : score >= 30 ? '#f97316' : '#ef4444';
  const r = 58; const circ = 2 * Math.PI * r;
  return (
    <div style={{ textAlign: 'center' }}>
      <svg width={150} height={150} viewBox="0 0 150 150">
        <defs>
          <linearGradient id="brandGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={score >= 75 ? '#00e676' : score >= 50 ? '#f97316' : '#ef4444'} />
          </linearGradient>
        </defs>
        <circle cx={75} cy={75} r={r} fill="none" stroke="#1e1e30" strokeWidth={10} />
        <circle cx={75} cy={75} r={r} fill="none" stroke="url(#brandGrad)" strokeWidth={10}
          strokeDasharray={circ} strokeDashoffset={circ - (score / 100) * circ}
          strokeLinecap="round" transform="rotate(-90 75 75)"
          style={{ transition: 'stroke-dashoffset 1.2s ease' }} />
        <text x={75} y={68} textAnchor="middle" fill="#fff" fontSize={36} fontWeight={800}>{score}</text>
        <text x={75} y={88} textAnchor="middle" fill="#9ca3af" fontSize={10} fontWeight={500}>BRAND SCORE</text>
      </svg>
    </div>
  );
}

function SubScoreBar({ label, score, color }: { label: string; score: number; color: string }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color }}>{score}</span>
      </div>
      <div style={{ height: 6, background: '#1e1e30', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${score}%`, background: color, borderRadius: 3, transition: 'width 1s ease' }} />
      </div>
    </div>
  );
}

function WeaknessPentagon({ map }: { map: { pricing: number; complexity: number; support: number; features: number; performance: number } }) {
  const labels = ['Pricing', 'Complexity', 'Support', 'Features', 'Performance'];
  const values = [map.pricing, map.complexity, map.support, map.features, map.performance];
  const maxVal = Math.max(...values, 1);
  const cx = 140, cy = 130, r = 72;
  const angles = [-90, -18, 54, 126, 198].map(a => a * Math.PI / 180);
  const outerPts = angles.map(a => ({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) }));
  const dataPts = angles.map((a, i) => ({ x: cx + (r * values[i] / maxVal) * Math.cos(a), y: cy + (r * values[i] / maxVal) * Math.sin(a) }));
  // Compute text anchor based on position relative to center
  const getAnchor = (px: number): 'start' | 'middle' | 'end' => {
    if (px < cx - 10) return 'end';
    if (px > cx + 10) return 'start';
    return 'middle';
  };
  return (
    <svg width={280} height={260} viewBox="0 0 280 260">
      {[0.33, 0.66, 1].map(pct => (
        <polygon key={pct} points={angles.map(a => `${cx + r * pct * Math.cos(a)},${cy + r * pct * Math.sin(a)}`).join(' ')} fill="none" stroke="#2a2a3e" strokeWidth={1} />
      ))}
      {outerPts.map((p, i) => <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#2a2a3e" strokeWidth={1} />)}
      <polygon points={dataPts.map(p => `${p.x},${p.y}`).join(' ')} fill="#ef444425" stroke="#ef4444" strokeWidth={2} />
      {outerPts.map((p, i) => {
        const lx = p.x + (p.x - cx) * 0.35;
        const ly = p.y + (p.y - cy) * 0.35 + 4;
        return (
          <text key={i} x={lx} y={ly}
            textAnchor={getAnchor(lx)} fill={values[i] > 0 ? '#ef4444' : '#6b7280'} fontSize={11} fontWeight={values[i] > 0 ? 700 : 400}>
            {labels[i]}{values[i] > 0 ? ` (${values[i]})` : ''}
          </text>
        );
      })}
    </svg>
  );
}

function ContentCalendarCard({ calendar }: { calendar: Array<{ week: number; topic: string; targetKeyword: string; contentType: string; why: string }> }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? calendar : calendar.slice(0, 8);
  return (
    <div className="card section-enter-d4">
      <h3 className="text-white font-semibold mb-3">12-Week Content Calendar</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="border-b border-[#2a2a3e]">
            <th className="text-left py-2 px-2 text-gray-500 font-medium">Week</th>
            <th className="text-left py-2 px-2 text-gray-500 font-medium">Topic</th>
            <th className="text-left py-2 px-2 text-gray-500 font-medium">Target Keyword</th>
            <th className="text-left py-2 px-2 text-gray-500 font-medium">Type</th>
          </tr></thead>
          <tbody>
            {visible.map((entry, i) => (
              <tr key={i} className="border-b border-[#2a2a3e] hover:bg-[#1a1a2e]">
                <td className="py-2 px-2 text-brand-500 font-bold">W{entry.week}</td>
                <td className="py-2 px-2 text-gray-300">{entry.topic.slice(0, 55)}{entry.topic.length > 55 ? '…' : ''}</td>
                <td className="py-2 px-2 text-white font-mono">{entry.targetKeyword}</td>
                <td className="py-2 px-2 text-gray-400">{entry.contentType}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {calendar.length > 8 && (
        <button
          onClick={() => setShowAll(!showAll)}
          style={{ marginTop: 8, background: 'none', border: 'none', color: '#7c6ff7', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
        >
          {showAll ? 'Show less' : `Show all ${calendar.length} weeks →`}
        </button>
      )}
    </div>
  );
}

function SidebarGroup({ label, defaultOpen = true, children }: { label: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 4 }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', padding: '4px 10px',
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#6b7280', fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.15em', marginBottom: 2, marginTop: 12,
        }}
      >
        <ChevronDown size={10} style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
        {label}
      </button>
      {open && children}
    </div>
  );
}

function SentimentGauge({ score }: { score: number }) {
  // Score: -100 to +100 → angle: 180° (left) to 0° (right)
  const pct = (score + 100) / 200; // 0..1
  const angleRad = Math.PI * (1 - pct); // π (left) to 0 (right)
  const cx = 120, cy = 110, r = 80;
  const nx = cx + r * Math.cos(angleRad), ny = cy - r * Math.sin(angleRad);
  const needleR = 60;
  const needleX = cx + needleR * Math.cos(angleRad), needleY = cy - needleR * Math.sin(angleRad);
  const color = score >= 50 ? '#22c55e' : score >= 0 ? '#f59e0b' : score >= -50 ? '#f97316' : '#ef4444';
  // Arc endpoint for the filled portion
  const largeArc = pct > 0.5 ? 1 : 0;
  return (
    <svg width={240} height={140} viewBox="0 0 240 140">
      {/* Background arc */}
      <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`} fill="none" stroke="#1e1e30" strokeWidth={14} />
      {/* Filled arc */}
      {pct > 0.005 && (
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 ${largeArc} 1 ${nx} ${ny}`} fill="none" stroke={color} strokeWidth={14} strokeLinecap="round" />
      )}
      {/* Needle */}
      <line x1={cx} y1={cy} x2={needleX} y2={needleY} stroke={color} strokeWidth={3} strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={5} fill={color} />
      {/* Labels */}
      <text x={cx - r - 4} y={cy + 14} textAnchor="middle" fill="#6b7280" fontSize={9}>-100</text>
      <text x={cx + r + 4} y={cy + 14} textAnchor="middle" fill="#6b7280" fontSize={9}>+100</text>
      <text x={cx} y={cy + 28} textAnchor="middle" fill={color} fontSize={18} fontWeight={700}>
        {score >= 0 ? '+' : ''}{score}
      </text>
    </svg>
  );
}

// ── CollapsiblePanel — progressive disclosure wrapper ──────────────
function CollapsiblePanel({ title, icon, defaultOpen = false, badge, children }: {
  title: string; icon?: string; defaultOpen?: boolean; badge?: string; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderRadius: 12, border: `1px solid ${open ? '#7c6ff730' : '#2a2a3e'}`, background: '#0d0d1f', transition: 'border-color 0.3s ease', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '14px 18px',
          background: 'none', border: 'none', cursor: 'pointer', color: '#e5e7eb',
        }}
      >
        <ChevronDown size={10} style={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.25s ease', color: '#7c6ff7' }} />
        {icon && <span style={{ fontSize: 14 }}>{icon}</span>}
        <span style={{ fontSize: 14, fontWeight: 600, flex: 1, textAlign: 'left' }}>{title}</span>
        {badge && (
          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#7c6ff715', color: '#7c6ff7', border: '1px solid #7c6ff730' }}>
            {badge}
          </span>
        )}
      </button>
      {open && (
        <div className="panel-content-enter" style={{ padding: '0 18px 18px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── InsightChip — cross-section insight pill ──────────────────────
function InsightChip({ text, icon = '' }: { text: string; icon?: string }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 14px', borderRadius: 20, fontSize: 12,
      background: '#7c6ff710', color: '#a5b4fc',
      border: '1px solid #7c6ff720', marginBottom: 12,
    }}>
      <span>{icon}</span>
      <span>{text}</span>
    </div>
  );
}

// ── Intel Loading Cinematic — step-by-step animated loader ──
const INTEL_LOADING_STEPS: Record<string, { stage: string; subtitle: string; steps: string[] }> = {
  nameValidation: {
    stage: 'Analyzing Your Brand',
    subtitle: 'Running deep brand intelligence across multiple signals',
    steps: ['Searching competitor brands…', 'Checking trademark databases…', 'Analyzing domain availability…', 'Scoring phonetics & memorability…', 'Building brand personality…', 'Generating strategic analysis…'],
  },
  keywords: {
    stage: 'Discovering Keywords',
    subtitle: 'Finding high-opportunity search terms your customers use',
    steps: ['Generating seed keywords…', 'Analyzing search volumes…', 'Scoring competition levels…', 'Identifying quick wins…', 'Building SEO roadmap…'],
  },
  competitors: {
    stage: 'Mapping the Competition',
    subtitle: 'Analyzing who you\'re up against and how to win',
    steps: ['Discovering competitors…', 'Extracting strengths & weaknesses…', 'Analyzing pricing models…', 'Detecting funding activity…', 'Generating battle cards…', 'Finding market gaps…', 'Building strategic playbook…'],
  },
  trends: {
    stage: 'Reading Market Signals',
    subtitle: 'Evaluating timing, growth, and opportunity windows',
    steps: ['Scanning industry news…', 'Pulling market size data…', 'Analyzing growth rates…', 'Checking investor signals…', 'Assessing market timing…'],
  },
  sentiment: {
    stage: 'Listening to the Market',
    subtitle: 'Uncovering what your audience really thinks and feels',
    steps: ['Gathering customer voices…', 'Analyzing pain points…', 'Extracting desires & wishes…', 'Mining copywriting gold…', 'Building messaging framework…', 'Generating product hints…'],
  },
};

function IntelLoadingCinematic({ sectionKey }: { sectionKey: string }) {
  const config = INTEL_LOADING_STEPS[sectionKey] ?? INTEL_LOADING_STEPS.nameValidation;
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const stepDuration = 2200;
    const timers: ReturnType<typeof setTimeout>[] = [];
    config.steps.forEach((_, i) => {
      timers.push(setTimeout(() => setActiveStep(i), i * stepDuration));
    });
    // Loop back to cycle through steps if still loading
    const totalDuration = config.steps.length * stepDuration;
    const loopTimer = setInterval(() => {
      setActiveStep(prev => (prev + 1) % config.steps.length);
    }, stepDuration);
    timers.push(setTimeout(() => {}, 0)); // placeholder
    // Smooth progress toward 95%
    let prog = 0;
    const tick = setInterval(() => {
      prog += (95 - prog) * 0.06;
      setProgress(Math.round(prog));
    }, 400);
    const hintTimer = setTimeout(() => setShowHint(true), 5000);
    return () => { timers.forEach(clearTimeout); clearInterval(loopTimer); clearInterval(tick); clearTimeout(hintTimer); };
  }, [config.steps]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 24px', animation: 'fadeIn 0.4s ease' }}>
      {/* Rotating gradient ring */}
      <div style={{ position: 'relative', width: 72, height: 72, marginBottom: 28 }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'conic-gradient(from 0deg, #7c6ff7, #00d4ff, rgba(124,111,247,0.1), #7c6ff7)', animation: 'spin 1.2s linear infinite', position: 'absolute' }} />
        <div style={{ width: 52, height: 52, background: '#0a0a1a', borderRadius: '50%', position: 'absolute', top: 10, left: 10 }} />
      </div>
      {/* Stage label */}
      <div style={{ fontSize: 20, fontWeight: 700, background: 'linear-gradient(135deg, #7c6ff7, #00d4ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: 6, textAlign: 'center' }}>
        {config.stage}
      </div>
      <div style={{ color: '#8888aa', fontSize: 13, marginBottom: 28, textAlign: 'center' }}>{config.subtitle}</div>
      {/* Steps list */}
      <div style={{ width: '100%', maxWidth: 340, marginBottom: 24 }}>
        {config.steps.map((step, i) => {
          const status = i < activeStep ? 'done' : i === activeStep ? 'active' : 'waiting';
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', opacity: status === 'waiting' ? 0.35 : 1, transition: 'opacity 0.3s ease' }}>
              <span style={{ fontSize: status === 'active' ? 15 : 13, color: status === 'done' ? '#00e676' : status === 'active' ? '#7c6ff7' : '#444466', width: 18, textAlign: 'center', flexShrink: 0, animation: status === 'active' ? 'glow-pulse 1s ease infinite' : undefined }}>
                {status === 'done' ? '✓' : status === 'active' ? '◉' : '○'}
              </span>
              <span style={{ fontSize: 13, color: status === 'done' ? '#e0e0f0' : status === 'active' ? '#f0f0ff' : '#8888aa', fontWeight: status === 'active' ? 600 : 400 }}>
                {step}{status === 'active' && <span style={{ color: '#7c6ff7' }}> ...</span>}
              </span>
            </div>
          );
        })}
      </div>
      {/* Progress bar */}
      <div style={{ width: '100%', maxWidth: 340, height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', marginBottom: 14 }}>
        <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg, #7c6ff7, #00d4ff)', borderRadius: 2, transition: 'width 0.6s ease' }} />
      </div>
      {showHint && (
        <div style={{ color: '#444466', fontSize: 12, animation: 'fadeIn 0.5s ease', textAlign: 'center' }}>
          AI is analyzing real market data — this usually takes 15-30 seconds…
        </div>
      )}
    </div>
  );
}

// ── extractPipelineContext — pull narrative context from pipeline ──
function extractPipelineContext(pipeline: Pipeline | null) {
  if (!pipeline) return { audience: null, problem: null, positioning: null, persona: null, threat: null, niche: null, monetization: null, elevatorPitch: null, uniqueMechanism: null, validationScore: null };
  const audience = pipeline.idea?.guidedAnswers?.whoSuffers || null;
  const problem = pipeline.idea?.guidedAnswers?.problemSolved || null;
  const positioning = (pipeline.positioning?.aiOutput as any)?.positioningStatement || null;
  const persona = (pipeline.positioning?.aiOutput as any)?.earlyAdopterPersona || null;
  const threat = (pipeline.realityCheck?.aiOutput as any)?.biggestThreat || null;
  const niche = pipeline.positioning?.userChoices?.selectedNiche || null;
  const monetization = pipeline.positioning?.userChoices?.selectedMonetization || null;
  const elevatorPitch = (pipeline.positioning?.aiOutput as any)?.elevatorPitch || null;
  const uniqueMechanism = (pipeline.positioning?.aiOutput as any)?.uniqueMechanism || null;
  const validationScore = (pipeline.realityCheck?.aiOutput as any)?.validationScore || null;
  return { audience, problem, positioning, persona, threat, niche, monetization, elevatorPitch, uniqueMechanism, validationScore };
}

// ── Constants ─────────────────────────────────────────────────────
const REC_COLORS = ['#22c55e', '#ef4444', '#f59e0b'];
const SENT_LABELS: Record<string, string> = { very_positive: 'Very Positive', positive: 'Positive', neutral: 'Neutral', negative: 'Negative', very_negative: 'Very Negative' };
const INTEL_TASKS = [
  { key: 'nameValidation' as const, endpoint: 'name', label: 'Brand Intelligence', cost: 2 },
  { key: 'keywords' as const, endpoint: 'keywords', label: 'Keywords', cost: 3 },
  { key: 'competitors' as const, endpoint: 'competitors', label: 'Competitors', cost: 5 },
  { key: 'trends' as const, endpoint: 'trends', label: 'Trends', cost: 3 },
  { key: 'sentiment' as const, endpoint: 'sentiment', label: 'Sentiment', cost: 3 },
];
const SIDEBAR_ICON: Record<string, React.ReactNode> = {
  overview: <BarChart2 size={15} />,
  validationPlan: <FlaskConical size={15} />,
  mvpBlueprint: <Hammer size={15} />,
  deepAnalysis: <BrainCircuit size={15} />,
  nameValidation: <ShieldCheck size={15} />,
  keywords: <Key size={15} />,
  competitors: <Swords size={15} />,
  trends: <TrendingUp size={15} />,
  sentiment: <MessageSquare size={15} />,
  feedback: <Users size={15} />,
};
const SIDEBAR_GROUPS = [
  { group: 'Core', items: [
    { key: 'overview', label: 'Overview' },
    { key: 'validationPlan', label: 'Validation Plan' },
    { key: 'mvpBlueprint', label: 'MVP Blueprint' },
    { key: 'deepAnalysis', label: 'Deep Analysis', pro: true },
  ]},
  { group: 'Intelligence', items: [
    { key: 'nameValidation', label: 'Brand Intelligence' },
    { key: 'keywords', label: 'Keywords' },
    { key: 'competitors', label: 'Competitors' },
    { key: 'trends', label: 'Trends' },
    { key: 'sentiment', label: 'Sentiment' },
  ]},
  { group: 'Traction', items: [
    { key: 'feedback', label: 'Beta & Feedback' },
  ]},
];
const SIDEBAR_ITEMS = SIDEBAR_GROUPS.flatMap(g => g.items);

function formatYAxis(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v}`;
}

// ── Main component ────────────────────────────────────────────────
export default function DashboardPage() {
  const { id: startupId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState('');
  const [isSavingSummary, setIsSavingSummary] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [intel, setIntel] = useState<IntelState>({
    nameValidation: initSection(), keywords: initSection(),
    competitors: initSection(), trends: initSection(), sentiment: initSection(),
  });
  const [runProgress, setRunProgress] = useState<{ current: number; total: number; currentLabel: string } | null>(null);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [kwSort, setKwSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'contentOpportunityScore', dir: 'desc' });
  const [kwFilter, setKwFilter] = useState('all');
  const [expandedCompetitor, setExpandedCompetitor] = useState<number | null>(null);
  const [pipelineData, setPipelineData] = useState<Pipeline | null>(null);
  const [validationChecklist, setValidationChecklist] = useState<{ day: string; completed: boolean; completedAt?: string }[]>([]);
  const [validationPlanData, setValidationPlanData] = useState<Record<string, unknown> | null>(null);
  const [activeMilestone, setActiveMilestone] = useState<typeof MILESTONES[keyof typeof MILESTONES] | null>(null);
  const [startupData, setStartupData] = useState<Record<string, any> | null>(null);
  const { setData: setReadinessData } = useReadinessStore();
  const { credits, fetchBalance } = useCreditsStore();
  const [creditModal, setCreditModal] = useState<{ required: number; available: number; action: string } | null>(null);
  const [renamingTo, setRenamingTo] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [creditConfirm, setCreditConfirm] = useState<{ key: keyof IntelState; endpoint: string; label: string; cost: number; force: boolean } | null>(null);
  // FIX: BUG 3 - state for batch "Run All" confirmation modal
  const [runAllConfirm, setRunAllConfirm] = useState<{ totalCost: number; pending: number } | null>(null);

  const { isSubscribed } = useSubscription();

  const overviewRef = useRef<HTMLDivElement>(null);
  const validationPlanRef = useRef<HTMLDivElement>(null);
  const deepAnalysisRef = useRef<HTMLDivElement>(null);
  const mvpBlueprintRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLDivElement>(null);
  const keywordsRef = useRef<HTMLDivElement>(null);
  const competitorsRef = useRef<HTMLDivElement>(null);
  const trendsRef = useRef<HTMLDivElement>(null);
  const sentimentRef = useRef<HTMLDivElement>(null);
  const feedbackRef = useRef<HTMLDivElement>(null);
  const sectionRefs: Record<string, React.RefObject<HTMLDivElement>> = {
    overview: overviewRef, validationPlan: validationPlanRef, deepAnalysis: deepAnalysisRef,
    mvpBlueprint: mvpBlueprintRef, nameValidation: nameRef, keywords: keywordsRef,
    competitors: competitorsRef, trends: trendsRef, sentiment: sentimentRef, feedback: feedbackRef,
  };

  const scrollTo = (key: string) => {
    setActiveSection(key);
    const el = sectionRefs[key]?.current;
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  useEffect(() => { document.title = 'Dashboard — StartupScore'; }, []);

  // ── Load dashboard + saved intelligence on mount ──────────────
  useEffect(() => {
    if (!startupId) return;
    (async () => {
      setIsLoading(true);
      try {
        const [dashRes, intelRes] = await Promise.all([
          api.get(`/dashboard/${startupId}`),
          api.get(`/intelligence/${startupId}`).catch(() => null),
        ]);
        setDashboard(dashRes.data.data.dashboard);
        setSummary(dashRes.data.data.dashboard.startup.founderSummary ?? '');
        // Load validation checklist & MVP blueprint from startup data
        const startupData = dashRes.data.data.dashboard.startup;
        setStartupData(startupData);
        if (startupData.validationChecklist) setValidationChecklist(startupData.validationChecklist);
        if (startupData.validationPlanData) {
          setValidationPlanData(startupData.validationPlanData as Record<string, unknown>);
        } else if (startupData.fromPipelineId) {
          // Auto-generate validation plan if pipeline is complete but plan is missing
          api.post(`/intelligence/${startupId}/validation-plan`).then(({ data: vpRes }) => {
            if (vpRes.success && vpRes.data) setValidationPlanData(vpRes.data as Record<string, unknown>);
          }).catch(() => {});
        }
        // Fetch matching pipeline if startup was created from one
        const fromId = dashRes.data.data.dashboard.startup.fromPipelineId;
        if (fromId) {
          api.get('/pipeline').then(({ data: plData }) => {
            const all: Pipeline[] = [
              plData.data.active,
              ...(plData.data.completed ?? [] as Pipeline[]),
              ...(plData.data.incubator ?? [] as Pipeline[]),
            ].filter(Boolean) as Pipeline[];
            const found = all.find(p => p.id === fromId);
            if (found) setPipelineData(found);
          }).catch(() => {});
        }
        if (intelRes) {
          const saved = intelRes.data.data as Record<string, { data: unknown; analyzedAt: string | null; status: string | null }>;
          setIntel(prev => {
            const next = { ...prev };
            for (const key of Object.keys(saved) as (keyof IntelState)[]) {
              const s = saved[key];
              if (s.status === 'complete' && s.data) {
                (next[key] as IntelSection<unknown>) = { status: 'done', data: s.data, error: '', analyzedAt: s.analyzedAt };
              }
            }
            return next;
          });
        }
        // Auto-scroll to name section if startup has a placeholder name
        const sName = startupData?.name || '';
        if (!sName || /^idea from /i.test(sName)) {
          setTimeout(() => {
            const el = nameRef.current;
            if (el) {
              const top = el.getBoundingClientRect().top + window.scrollY - 90;
              window.scrollTo({ top, behavior: 'smooth' });
              setActiveSection('nameValidation');
            }
          }, 500);
        }
      } catch (err) {
        setError(extractApiError(err).message);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [startupId]);

  // FIX: PRIORITY 4 - always show credit confirmation before spending (including re-analyze)
  const runSection = (key: keyof IntelState, endpoint: string, force = false) => {
    // If already done and not forcing, return cached (no charge)
    if (!force && intel[key].status === 'done') {
      runSectionDirect(key, endpoint, false);
      return;
    }
    // Show credit confirmation before any analysis that will charge
    const task = INTEL_TASKS.find(t => t.key === key);
    const cost = task?.cost ?? 0;
    // UX 7: Skip confirmation if user checked "Don't ask again"
    if (cost > 0 && localStorage.getItem('sf_skip_credit_confirm') !== '1') {
      setCreditConfirm({ key, endpoint, label: task?.label ?? key, cost, force });
      return;
    }
    runSectionDirect(key, endpoint, force);
  };

  const runSectionDirect = async (key: keyof IntelState, endpoint: string, force = false) => {
    setIntel(prev => ({ ...prev, [key]: { ...prev[key], status: 'loading', error: '' } }));
    const MAX_RETRIES = 1;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) await new Promise(r => setTimeout(r, 1500 * attempt));
        const { data } = await aiApi.post(`/intelligence/${startupId}/${endpoint}`, { force });
        setIntel(prev => ({ ...prev, [key]: { status: 'done', data: data.data, error: '', analyzedAt: new Date().toISOString() } }));
        fetchBalance();
        return;
      } catch (err: any) {
        const is5xx = err?.response?.status >= 500;
        const is429 = err?.response?.status === 429;
        const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout');
        if ((is5xx || is429 || isTimeout) && attempt < MAX_RETRIES) continue;
        if (err?.response?.status === 402) {
          const d = err.response.data;
          setCreditModal({ required: d.required, available: d.available, action: d.action });
          setIntel(prev => ({ ...prev, [key]: { ...prev[key], status: 'idle', error: '' } }));
        } else {
          setIntel(prev => ({ ...prev, [key]: { ...prev[key], status: 'error',
            error: is429 ? 'High demand right now — please wait 30 seconds and try again.' : extractApiError(err).message } }));
        }
        return;
      }
    }
  };

  // FIX: BUG 3 - show confirmation modal instead of window.confirm
  const runAllSequential = async () => {
    const pendingTasks = INTEL_TASKS.filter(t => intel[t.key].status !== 'done');
    const totalCost = pendingTasks.reduce((s, t) => s + t.cost, 0);
    if (totalCost > 0 && !runAllConfirm) {
      setRunAllConfirm({ totalCost, pending: pendingTasks.length });
      return;
    }
    setRunAllConfirm(null);
    setRunProgress({ current: 0, total: INTEL_TASKS.length, currentLabel: '' });
    for (let i = 0; i < INTEL_TASKS.length; i++) {
      const task = INTEL_TASKS[i];
      setRunProgress({ current: i, total: INTEL_TASKS.length, currentLabel: task.label });
      setIntel(prev => ({ ...prev, [task.key]: { ...prev[task.key], status: 'loading', error: '' } }));
      try {
        const { data } = await aiApi.post(`/intelligence/${startupId}/${task.endpoint}`, { force: false });
        setIntel(prev => ({ ...prev, [task.key]: { status: 'done', data: data.data, error: '', analyzedAt: new Date().toISOString() } }));
      } catch (err: any) {
        if (err?.response?.status === 402) {
          const d = err.response.data;
          setCreditModal({ required: d.required, available: d.available, action: d.action });
          setIntel(prev => ({ ...prev, [task.key]: { ...prev[task.key], status: 'idle', error: '' } }));
          setRunProgress(null);
          return;
        }
        setIntel(prev => ({ ...prev, [task.key]: { ...prev[task.key], status: 'error', error: extractApiError(err).message } }));
      }
      if (i < INTEL_TASKS.length - 1) await new Promise(r => setTimeout(r, 500));
    }
    setRunProgress({ current: INTEL_TASKS.length, total: INTEL_TASKS.length, currentLabel: 'Complete' });
    setTimeout(() => setRunProgress(null), 3000);
  };

  const copyText = (text: string, idx: number) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 2000);
    });
  };

  const handleSaveSummary = async () => {
    setIsSavingSummary(true);
    try { await api.put(`/startups/${startupId}`, { founderSummary: summary }); }
    catch { /* ignore */ }
    finally { setIsSavingSummary(false); }
  };

  // [Bug 2] Check if startup has a placeholder name
  const isPlaceholderName = !startupData?.name || /^idea from /i.test(startupData.name);

  const handleRename = async () => {
    const trimmed = renamingTo.trim();
    if (!trimmed || trimmed.length < 2) return;
    setIsRenaming(true);
    try {
      await api.put(`/startups/${startupId}`, { name: trimmed });
      setStartupData(prev => prev ? { ...prev, name: trimmed } : prev);
      setRenamingTo('');
      // FIX: BUG 1 - correct endpoint from 'validate-name' to 'name'
      setTimeout(() => runSection('nameValidation', 'name'), 300);
    } catch { /* ignore */ }
    finally { setIsRenaming(false); }
  };

  // ── Readiness score — push to global store whenever data changes ──
  useEffect(() => {
    if (!dashboard || !startupId) return;
    const { feedbackAnalytics: fa, betaUsers, baseScenario: bs } = dashboard;
    const items: ReadinessItem[] = [
      { id: 'pipeline', label: 'Complete validation pipeline', points: 20, done: pipelineData?.status === 'completed', link: '/pipeline' },
      ...INTEL_TASKS.map(t => ({
        id: `intel_${t.key}`, label: `${t.label} analysis`, points: 8,
        done: intel[t.key].status === 'done', link: `/startups/${startupId}/dashboard`,
      })),
      { id: 'beta', label: 'Add beta testers', points: 10, done: (betaUsers?.length ?? 0) > 0, link: `/startups/${startupId}/beta` },
      { id: 'feedback', label: 'Collect 10+ feedback responses', points: 10, done: fa.totalResponses >= 10, link: `/startups/${startupId}/feedback` },
      { id: 'summary', label: "Write Founder's Summary", points: 10, done: summary.trim().length > 10, link: `/startups/${startupId}/dashboard` },
      { id: 'financials', label: 'Set up financial scenario', points: 10, done: bs !== null, link: `/startups/${startupId}/financials` },
    ];
    setReadinessData(items, startupId);
  }, [dashboard, pipelineData, intel, summary, startupId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Milestone logic ─────────────────────────────────────────────
  const triggerMilestone = async (milestoneId: string) => {
    try {
      const res = await api.post(`/startups/${startupId}/milestone`, { milestoneId });
      if (res.data.data.creditsAwarded > 0) fetchBalance();
      setActiveMilestone(MILESTONES[milestoneId] ?? null);
    } catch { /* ignore duplicates */ }
  };

  const checkMilestones = async () => {
    if (!startupData || !startupId) return;
    const achieved: string[] = startupData.milestones ?? [];
    if (!achieved.includes('first_signal') && (startupData.signals?.length ?? 0) >= 1) {
      await triggerMilestone('first_signal');
    }
    const startDate = startupData.validationStartDate ? new Date(startupData.validationStartDate) : null;
    const currentDay = startDate ? Math.floor((Date.now() - startDate.getTime()) / 86400000) + 1 : 0;
    if (!achieved.includes('week_one') && currentDay >= 7) {
      await triggerMilestone('week_one');
    }
    if (!achieved.includes('mvp_unlocked') && startupData.mvpBlueprint?.status === 'complete') {
      await triggerMilestone('mvp_unlocked');
    }
    if (!achieved.includes('first_intelligence') &&
        (intel.nameValidation.status === 'done' || intel.keywords.status === 'done' || intel.competitors.status === 'done')) {
      await triggerMilestone('first_intelligence');
    }
  };

  // Check milestones after data loads and after intel changes
  useEffect(() => { checkMilestones(); }, [startupData, intel]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers for new components ──────────────────────────────────
  const handleTaskToggle = async (taskId: string, completed: boolean) => {
    try {
      await api.patch(`/startups/${startupId}/validation-progress`, { taskId, completed });
      setStartupData(prev => prev ? {
        ...prev,
        validationProgress: { ...(prev.validationProgress ?? {}), [taskId]: completed },
      } : prev);
    } catch { /* ignore */ }
  };

  const handleGenerateBlueprint = async (results: Record<string, any>) => {
    try {
      const { data } = await api.post(`/startups/${startupId}/generate-mvp-blueprint`, results, { timeout: 120000 });
      const bp = data.data.mvpBlueprint;
      setStartupData(prev => prev ? { ...prev, mvpBlueprint: bp, validationResults: { ...results, loggedAt: new Date().toISOString() } } : prev);
      fetchBalance();
      // FIX: #8 — clear validation deadline after results submitted
      localStorage.removeItem(`validationDeadline_${startupId}`);
      // Check MVP milestone
      await triggerMilestone('mvp_unlocked');
    } catch (err: any) {
      throw err;
    }
  };

  const handleSignalLogged = (_signal: any, _analysis: any) => {
    // Refresh startup data to update signal count
    setStartupData(prev => {
      if (!prev) return prev;
      const newSignals = [...(prev.signals ?? []), _signal];
      return { ...prev, signals: newSignals, signalAnalysis: _analysis ?? prev.signalAnalysis };
    });
  };

  // ── Section header helper (called as function, not component) ──
  // FIX: BUG 2 - credit badge labels now match actual CREDIT_COSTS
  const INTEL_CREDIT_MAP: Record<string, number> = {
    nameValidation: 2, keywords: 3, competitors: 5, trends: 3, sentiment: 3,
  };
  const SECTION_ICON: Record<string, React.ReactNode> = {
    nameValidation: <ShieldCheck size={18} className="text-brand-500" />,
    keywords: <Key size={18} className="text-brand-500" />,
    competitors: <Swords size={18} className="text-brand-500" />,
    trends: <TrendingUp size={18} className="text-brand-500" />,
    sentiment: <MessageSquare size={18} className="text-brand-500" />,
  };

  const sectionHeader = (sectionKey: keyof IntelState, endpoint: string, title: string) => {
    const s = intel[sectionKey];
    const creditCost = INTEL_CREDIT_MAP[sectionKey] ?? 1;
    const insufficient = credits < creditCost;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 className="flex items-center gap-2 text-xl font-bold text-white">{SECTION_ICON[sectionKey]}{title}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {s.analyzedAt && s.status === 'done' && (
            <span style={{ fontSize: 12, color: '#6b7280' }}>
              Last analyzed {timeAgo(s.analyzedAt)} ·{' '}
              <button onClick={() => runSection(sectionKey, endpoint, true)} style={{ color: '#7c6ff7', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>
                Re-analyze ({creditCost} credit{creditCost > 1 ? 's' : ''}) →
              </button>
            </span>
          )}
          {(s.status === 'idle' || s.status === 'error') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={() => runSection(sectionKey, endpoint, false)}
                disabled={insufficient}
                className="btn-primary"
                style={{ fontSize: 12, padding: '4px 12px', opacity: insufficient ? 0.5 : 1, cursor: insufficient ? 'not-allowed' : 'pointer' }}
                title={insufficient ? `Need ${creditCost} credits — you have ${credits}` : undefined}
              >
                Analyze
              </button>
              <span style={{ fontSize: 10, background: '#374151', color: '#d1d5db', borderRadius: 10, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                {creditCost} cr
              </span>
            </div>
          )}
          {s.status === 'loading' && <span style={{ fontSize: 12, color: '#f59e0b' }}>Analyzing...</span>}
        </div>
      </div>
    );
  };

  if (isLoading) return (
    <div className="space-y-6">
      <div className="grid grid-cols-4 gap-4">{[1, 2, 3, 4].map(i => <SkeletonMetricCard key={i} />)}</div>
      <SkeletonChart />
    </div>
  );

  if (error) return (
    <div className="card text-center py-12">
      <p className="text-red-400 mb-4">{error}</p>
      <button onClick={() => window.location.reload()} className="btn-primary">Retry</button>
    </div>
  );

  if (!dashboard) return null;

  // FIX: #10 — standardized locked section with navigation link
  // ── Pipeline lock — dashboard locked until pipeline step 5 completed ──
  const pipelineIncomplete = pipelineData && pipelineData.currentStep < 5;
  if (pipelineIncomplete) {
    const stepsLeft = 5 - (pipelineData?.currentStep ?? 0);
    return (
      <div style={{ position: 'relative', minHeight: '80vh' }}>
        {/* Blurred placeholder */}
        <div style={{
          filter: 'blur(8px)', opacity: 0.3, pointerEvents: 'none',
          padding: 24,
        }}>
          <div className="dash-locked-grid">
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ background: '#0d0d1f', borderRadius: 16, height: 100, border: '1px solid rgba(255,255,255,0.06)' }} />
            ))}
          </div>
          <div style={{ background: '#0d0d1f', borderRadius: 16, height: 300, border: '1px solid rgba(255,255,255,0.06)' }} />
        </div>

        {/* Lock overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10,
        }}>
          <div style={{
            background: 'rgba(13,13,31,0.95)', border: '1px solid rgba(124,111,247,0.3)',
            borderRadius: 24, textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }} className="dash-lock-card">
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 8 }}>
              Dashboard Locked
            </h2>
            <p style={{ fontSize: 14, color: '#9ca3af', marginBottom: 24, lineHeight: 1.6 }}>
              Complete your validation pipeline to unlock the full dashboard
              with market intelligence, competitor analysis, and more.
            </p>
            <div style={{
              background: 'rgba(124,111,247,0.08)', border: '1px solid rgba(124,111,247,0.2)',
              borderRadius: 12, padding: '12px 20px', marginBottom: 24,
              fontSize: 13, color: '#a89df5',
            }}>
              {stepsLeft} step{stepsLeft !== 1 ? 's' : ''} remaining in your pipeline
            </div>
            <button
              onClick={() => navigate(`/pipeline/${pipelineData?.id}`)}
              style={{
                padding: '12px 32px', borderRadius: 12,
                background: '#f97316', border: 'none',
                color: '#fff', fontSize: 15, fontWeight: 700,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
            >
              Continue Pipeline →
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { startup, metrics, baseScenario, scenarios, feedbackAnalytics, topFeatureRequests, betaUsers } = dashboard;
  const projections = baseScenario?.projections?.slice(0, 12) ?? [];
  const npsColor = metrics.betaNPS > 70 ? '#a855f7' : metrics.betaNPS > 30 ? '#22c55e' : metrics.betaNPS >= 0 ? '#f59e0b' : '#ef4444';
  const recData = [
    { name: 'Would Recommend', value: feedbackAnalytics.recommendationRate.yes },
    { name: 'Would Not', value: feedbackAnalytics.recommendationRate.no },
    { name: 'Maybe', value: feedbackAnalytics.recommendationRate.maybe },
  ];
  const scenarioTable = scenarios.filter(s => s.summary).map(s => ({
    name: s.name,
    rev6m: s.projections.find(m => m.month === 6)?.revenue ?? 0,
    rev12m: s.projections.find(m => m.month === 12)?.revenue ?? 0,
    breakEven: s.summary?.breakEvenMonth ?? null,
  }));
  const isRunningAny = INTEL_TASKS.some(t => intel[t.key].status === 'loading');
  const doneCount = INTEL_TASKS.filter(t => intel[t.key].status === 'done').length;

  const kwData = intel.keywords.data?.keywords ?? [];
  // P1-11: Weighted score = 40% opportunity + 25% volume + 25% easiness + 10% intent
  const VOLUME_SCORES: Record<string, number> = { high: 10, medium: 6, low: 3 };
  const DIFF_SCORES: Record<string, number> = { easy: 10, medium: 5, hard: 2 };
  const INTENT_SCORES: Record<string, number> = { transactional: 10, commercial: 8, informational: 4, navigational: 3 };
  const computeWeightedScore = (kw: KwItem) => {
    const opp = kw.contentOpportunityScore ?? 5;
    const vol = VOLUME_SCORES[kw.volumeEstimate] ?? 5;
    const ease = DIFF_SCORES[kw.difficulty] ?? 5;
    const intent = INTENT_SCORES[kw.intent] ?? 4;
    return Math.round((opp * 0.4 + vol * 0.25 + ease * 0.25 + intent * 0.1) * 10) / 10;
  };
  const filteredKw = kwFilter === 'all' ? kwData : kwData.filter(k => k.category === kwFilter);
  const sortedKw = [...filteredKw].sort((a, b) => {
    if (kwSort.col === 'weightedScore') {
      const diff = computeWeightedScore(a) - computeWeightedScore(b);
      return kwSort.dir === 'asc' ? diff : -diff;
    }
    const av = a[kwSort.col as keyof KwItem], bv = b[kwSort.col as keyof KwItem];
    if (typeof av === 'number' && typeof bv === 'number') return kwSort.dir === 'asc' ? av - bv : bv - av;
    return kwSort.dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  return (
    <>
    <div>
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4" style={{ justifyContent: 'space-between', flexWrap: 'wrap', overflow: 'hidden', maxWidth: '100%' }}>
        <div className="flex items-center gap-2" style={{ minWidth: 0, overflow: 'hidden' }}>
          <Link to="/startups" className="hover:text-white">My Startups</Link>
          <span>/</span>
          {editingTitle ? (
            <input
              autoFocus
              defaultValue={startup.name}
              onBlur={async (e) => {
                const val = e.target.value.trim();
                if (val && val.length >= 2 && val !== startup.name) {
                  try {
                    await api.put(`/startups/${startupId}`, { name: val });
                    setStartupData(prev => prev ? { ...prev, name: val } : prev);
                  } catch { /* ignore */ }
                }
                setEditingTitle(false);
              }}
              onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') setEditingTitle(false); }}
              style={{
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(124,111,247,0.4)',
                borderRadius: 6, color: '#fff', fontSize: 13, padding: '2px 8px', width: 180,
                outline: 'none',
              }}
            />
          ) : (
            <button
              onClick={() => setEditingTitle(true)}
              className="hover:text-white"
              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 'inherit' }}
              title="Click to rename"
            >
              {startup.name}
            </button>
          )}
          <span>/</span>
          <span className="text-gray-300">Dashboard</span>
        </div>
        <div className="hidden sm:flex">
          {startupData && (
            <ShareProgressButton
              startup={{ ...startup, id: startupId }}
              currentDay={startupData.validationStartDate ? Math.min(Math.floor((Date.now() - new Date(startupData.validationStartDate).getTime()) / 86400000) + 1, 14) : 1}
              signalCount={startupData.signals?.length ?? 0}
            />
          )}
        </div>
      </div>

      {/* FIX: #8 — 14-day validation reminder banner using localStorage deadline */}
      {(() => {
        const hasResults = !!startupData?.validationResults?.loggedAt || !!startupData?.validationResults?.submittedAt;
        if (hasResults) return null;
        const deadline = parseInt(localStorage.getItem(`validationDeadline_${startupId}`) || '0');
        if (deadline <= 0) return null;
        const isNearDeadline = Date.now() >= deadline - 2 * 24 * 60 * 60 * 1000;
        const isPastDeadline = Date.now() >= deadline;
        const daysLeft = Math.max(0, Math.ceil((deadline - Date.now()) / 86400000));
        if (isPastDeadline) return (
          <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 12, padding: '12px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#ef4444' }}>Overdue</span>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#ef4444' }}>Your 14-day validation period has ended</p>
              <p style={{ fontSize: 12, color: '#9ca3af' }}>Submit your validation results to unlock your personalized MVP Blueprint.</p>
            </div>
            <button onClick={() => scrollTo('mvpBlueprint')} style={{ background: '#ef444420', border: '1px solid #ef444440', borderRadius: 8, color: '#ef4444', fontSize: 12, fontWeight: 600, padding: '6px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Log Results →</button>
          </div>
        );
        if (isNearDeadline) return (
          <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 12, padding: '12px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#f59e0b' }}>Deadline</span>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b' }}>{daysLeft} day{daysLeft !== 1 ? 's' : ''} left in your validation period</p>
              <p style={{ fontSize: 12, color: '#9ca3af' }}>Start collecting your results — your validation deadline is approaching.</p>
            </div>
            <button onClick={() => scrollTo('mvpBlueprint')} style={{ background: '#f59e0b20', border: '1px solid #f59e0b40', borderRadius: 8, color: '#f59e0b', fontSize: 12, fontWeight: 600, padding: '6px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}>Log Results →</button>
          </div>
        );
        return null;
      })()}

      <div className="dash-layout">
        {/* ── Sidebar (hidden on mobile via CSS) ── */}
        <aside className="dash-sidebar">
          {SIDEBAR_GROUPS.map(group => (
            <SidebarGroup key={group.group} label={group.group} defaultOpen>
              {group.items.map(item => {
                const task = INTEL_TASKS.find(t => t.key === item.key);
                const status = task ? intel[task.key]?.status : undefined;
                return (
                  <button key={item.key} onClick={() => scrollTo(item.key)} className={activeSection === item.key ? 'nav-item-active' : ''} style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px',
                    borderRadius: 8, background: activeSection === item.key ? '#2a2a3e' : 'transparent',
                    border: 'none', color: activeSection === item.key ? '#fff' : '#9ca3af',
                    fontSize: 13, fontWeight: activeSection === item.key ? 600 : 400,
                    cursor: 'pointer', marginBottom: 2, textAlign: 'left', position: 'relative',
                  }}>
                    <span style={{ display: 'flex', flexShrink: 0 }}>{SIDEBAR_ICON[item.key]}</span>
                    <span style={{ flex: 1 }}>{item.label}</span>
                    {(item as any).pro && !isSubscribed && (
                      <span className="badge-shimmer" style={{
                        fontSize: 9, fontWeight: 800, padding: '1px 5px', borderRadius: 4,
                        color: '#f97316',
                        textTransform: 'uppercase', letterSpacing: 0.5,
                      }}>PRO</span>
                    )}
                    {task && (
                      <span style={{ fontSize: 10, borderRadius: 4, padding: '1px 5px',
                        background: status === 'done' ? '#22c55e20' : status === 'loading' ? '#f59e0b20' : status === 'error' ? '#ef444420' : '#2a2a3e',
                        color: status === 'done' ? '#22c55e' : status === 'loading' ? '#f59e0b' : status === 'error' ? '#ef4444' : '#6b7280',
                      }}>
                        {status === 'done' ? '✓' : status === 'loading' ? '…' : status === 'error' ? '!' : '–'}
                      </span>
                    )}
                  </button>
                );
              })}
            </SidebarGroup>
          ))}
          <div style={{ borderTop: '1px solid #2a2a3e', margin: '12px 0' }} />
          {/* Financials link with lesson chip */}
          <button onClick={() => navigate(`/startups/${startupId}/financials`)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', borderRadius: 8, background: 'transparent', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
            <LineChartIcon size={15} />
            <span style={{ flex: 1 }}>Financials</span>
            <span onClick={e => { e.stopPropagation(); navigate('/course?lesson=5'); }} style={{
              fontSize: 9, background: '#6366f120', color: '#818cf8', borderRadius: 4, padding: '1px 5px',
              fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
            }}>L5</span>
          </button>
          {/* Beta & Feedback link with lesson chip */}
          <button onClick={() => navigate(`/startups/${startupId}/beta`)} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', borderRadius: 8, background: 'transparent', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
            <UserCheck size={15} />
            <span style={{ flex: 1 }}>Beta Users</span>
            <span onClick={e => { e.stopPropagation(); navigate('/course?lesson=6'); }} style={{
              fontSize: 9, background: '#6366f120', color: '#818cf8', borderRadius: 4, padding: '1px 5px',
              fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
            }}>L6</span>
          </button>
          <Link to="/course" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, color: '#9ca3af', fontSize: 13, textDecoration: 'none' }}>
            <BookOpen size={15} />
            <span style={{ flex: 1 }}>Learning Center</span>
          </Link>
          <button onClick={() => navigate('/startups')} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', borderRadius: 8, background: 'transparent', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', marginTop: 4 }}>
            <ArrowLeft size={13} /><span>Back to startups</span>
          </button>
        </aside>

        {/* ── Main content ── */}
        <div className="dash-main">

          {/* ── Mobile section nav (visible only on mobile when sidebar hidden) ── */}
          <div className="md:hidden" style={{
            display: 'flex', gap: 6, overflowX: 'auto', padding: '8px 0 12px',
            WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', maxWidth: '100%',
          }}>
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'nameValidation', label: 'Intel' },
              { key: 'validationPlan', label: 'Validate' },
              { key: 'mvpBlueprint', label: 'MVP' },
            ].map(item => (
              <button key={item.key} onClick={() => scrollTo(item.key)} style={{
                flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                background: activeSection === item.key ? 'rgba(124,111,247,0.15)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${activeSection === item.key ? 'rgba(124,111,247,0.3)' : 'rgba(255,255,255,0.08)'}`,
                color: activeSection === item.key ? '#7c6ff7' : '#9ca3af',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}>
                {SIDEBAR_ICON[item.key]} {item.label}
              </button>
            ))}
          </div>

          {/* ── OVERVIEW ── */}
          <div ref={overviewRef} className="space-y-6 stagger-fade-up" style={{ paddingTop: 8 }}>

            {/* VALUE 1: "What You Unlocked" banner — shown once per startup (persistently dismissible) */}
            {startupId && !(startupData?.hasSeenDashboardWelcome) && !localStorage.getItem(`sf_dashboard_welcomed_${startupId}`) && pipelineData && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(0,230,118,0.06) 0%, rgba(0,212,255,0.06) 100%)',
                border: '1px solid rgba(0,230,118,0.25)',
                borderRadius: 16, padding: '20px 24px',
                position: 'relative',
              }}>
                <button
                  onClick={() => {
                    localStorage.setItem(`sf_dashboard_welcomed_${startupId}`, '1');
                    api.patch(`/startups/${startupId}/seen-dashboard-welcome`).catch(() => {});
                    setStartupData(prev => prev ? { ...prev, hasSeenDashboardWelcome: true } : prev);
                  }}
                  style={{ position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 16 }}
                >✕</button>
                <h3 style={{ fontSize: 16, fontWeight: 800, color: '#fff', marginBottom: 10 }}>
                  Your validation report is ready!
                </h3>
                <p style={{ fontSize: 14, color: '#fff', marginBottom: 14, lineHeight: 1.5 }}>
                  In ~27 minutes, you did what would take a consultant 3 weeks.
                </p>
                <p style={{ fontSize: 13, color: '#d1d5db', marginBottom: 14 }}>You now have:</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
                  {['✓ Founder profile', '✓ Clarity score', '✓ Reality check', '✓ Positioning strategy', '✓ 14-day validation plan', '✓ Outreach messages'].map((item, i) => (
                    <span key={i} style={{
                      fontSize: 13, color: '#00e676',
                      background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.2)',
                      borderRadius: 8, padding: '4px 12px',
                    }}>{item}</span>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 0 }}>
                  Next: Run Day 1 of your plan. Come back with results to unlock your MVP Blueprint.
                </p>
                <button
                  className="btn-primary"
                  style={{
                    width: '100%', marginTop: 16, padding: '12px 24px',
                    borderRadius: 12, fontSize: 15, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  }}
                  onClick={() => {
                    localStorage.setItem(`sf_dashboard_welcomed_${startupId}`, '1');
                    setStartupData(prev => prev ? { ...prev, hasSeenDashboardWelcome: true } : prev);
                    api.patch(`/startups/${startupId}/seen-dashboard-welcome`).catch(() => {});
                    setTimeout(() => {
                      document.getElementById('validation-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                >
                  Start Day 1 of Your Validation Plan →
                </button>
              </div>
            )}

            {/* FEATURE 1: Low credit upsell banner when credits ≤ 3 */}
            {credits <= 3 && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(249,115,22,0.08) 0%, rgba(239,68,68,0.08) 100%)',
                border: '1px solid rgba(249,115,22,0.3)',
                borderRadius: 12, padding: '14px 20px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
              }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#f97316' }}>Low credits!</span>
                  <span style={{ fontSize: 13, color: '#9ca3af', marginLeft: 8 }}>
                    You have {credits} credit{credits !== 1 ? 's' : ''} remaining. Top up to keep analyzing.
                  </span>
                </div>
                <button onClick={() => navigate('/credits')} style={{
                  background: '#f97316', border: 'none', borderRadius: 8, color: '#fff',
                  fontSize: 12, fontWeight: 700, padding: '8px 16px', cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                  Buy Credits
                </button>
              </div>
            )}

            {/* P1-15: Readiness progress tip for 40-74% */}
            {(() => {
              const { score, checklist } = useReadinessStore.getState();
              if (score < 40 || score >= 75 || checklist.length === 0) return null;
              const remaining = checklist.filter(i => !i.done);
              const topRemaining = remaining.slice(0, 2);
              return (
                <div style={{
                  background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.25)',
                  borderRadius: 12, padding: '14px 20px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
                }}>
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#3b82f6' }}>
                      {score}% market-ready
                    </span>
                    <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 8 }}>
                      Next: {topRemaining.map(i => i.label).join(' · ')}
                    </span>
                  </div>
                  <button onClick={() => useReadinessStore.getState().openPanel()} style={{
                    background: 'none', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 8,
                    color: '#3b82f6', fontSize: 12, fontWeight: 600, padding: '5px 14px', cursor: 'pointer',
                  }}>
                    View Scorecard
                  </button>
                </div>
              );
            })()}

            {/* P1-8: "#1 Priority Right Now" card — sequenced by state */}
            {(() => {
              const { score } = useReadinessStore.getState();
              if (score >= 100) return null;

              // Sequenced priority: intel first, then validation
              if (doneCount === 0) {
                return (
                  <div style={{
                    background: 'rgba(249,115,22,0.04)',
                    border: '2px solid rgba(249,115,22,0.35)',
                    borderRadius: 14, padding: '16px 20px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 200 }}>
                      <BrainCircuit size={24} style={{ color: '#f97316', flexShrink: 0 }} />
                      <div>
                        <p style={{ fontSize: 10, color: '#f97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2, borderLeft: '2px solid #f97316', paddingLeft: 8 }}>
                          Your #1 Priority Right Now
                        </p>
                        <p style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Run Brand Intelligence first — it takes 2 minutes and the AI does the work.</p>
                      </div>
                    </div>
                    <button
                      onClick={() => scrollTo('nameValidation')}
                      className="btn-primary"
                      style={{ fontSize: 13, padding: '8px 18px', whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Do this now →
                    </button>
                  </div>
                );
              }

              return (
                <div style={{
                  background: 'rgba(249,115,22,0.04)',
                  border: '2px solid rgba(249,115,22,0.35)',
                  borderRadius: 14, padding: '16px 20px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 200 }}>
                    <ClipboardList size={24} style={{ color: '#f97316', flexShrink: 0 }} />
                    <div>
                      <p style={{ fontSize: 10, color: '#f97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2, borderLeft: '2px solid #f97316', paddingLeft: 8 }}>
                        Your #1 Priority Right Now
                      </p>
                      <p style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Start Day 1: Build your landing page</p>
                    </div>
                  </div>
                  <button
                    onClick={() => scrollTo('validationPlan')}
                    className="btn-primary"
                    style={{ fontSize: 13, padding: '8px 18px', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    Open Checklist →
                  </button>
                </div>
              );
            })()}

            <StartupBriefCard
              startup={startup}
              startupId={startupId!}
              pipeline={pipelineData}
              isRunningAny={isRunningAny}
              category={startupData?.category ?? ''}
              onCategoryChange={(cat) => setStartupData(prev => prev ? { ...prev, category: cat } : prev)}
              onRunIntelligence={runAllSequential}
              // FIX: BUG 5 - PDF export via window.print() with full report overlay
              onPrint={() => {
                const esc = (s: unknown) => typeof s === 'string' ? s.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';
                const lines: string[] = [];
                lines.push(`<h1>${esc(startup.name)}</h1>`);
                lines.push(`<p class="print-meta">StartupScore Report — Generated ${new Date().toLocaleDateString()} — startupscore.live</p>`);
                if (startup.description) lines.push(`<p>${esc(startup.description)}</p>`);
                if (startup.targetSegment) lines.push(`<p><strong>Target:</strong> ${esc(startup.targetSegment)}</p>`);
                if (startup.problemStatement) lines.push(`<p><strong>Problem:</strong> ${esc(startup.problemStatement)}</p>`);
                if (startup.uniqueValueProposition) lines.push(`<p><strong>UVP:</strong> ${esc(startup.uniqueValueProposition)}</p>`);
                // Pipeline data
                if (pipelineData?.idea?.aiOutput) {
                  const idea = pipelineData.idea.aiOutput as Record<string, unknown>;
                  if (idea.ideaSnapshot) lines.push(`<h2>Idea Snapshot</h2><p>${esc(idea.ideaSnapshot)}</p>`);
                }
                if (pipelineData?.realityCheck?.aiOutput) {
                  const rc = pipelineData.realityCheck.aiOutput as Record<string, unknown>;
                  if (rc.verdict) lines.push(`<h2>Reality Check: ${esc(rc.verdict)}</h2>`);
                  if (rc.validationScore) lines.push(`<p><strong>Validation Score:</strong> ${rc.validationScore}/100</p>`);
                }
                if (pipelineData?.positioning?.aiOutput) {
                  const pos = pipelineData.positioning.aiOutput as Record<string, unknown>;
                  if (pos.positioningStatement) lines.push(`<h2>Positioning</h2><p>${esc(pos.positioningStatement)}</p>`);
                  if (pos.elevatorPitch) lines.push(`<p><strong>Elevator Pitch:</strong> ${esc(pos.elevatorPitch)}</p>`);
                }
                // Intelligence highlights
                const nv = intel.nameValidation;
                if (nv.status === 'done' && nv.data) {
                  if (nv.data.brandScore) {
                    lines.push(`<h2>Brand Intelligence</h2><p>Brand Score: ${nv.data.brandScore.overall}/100 (${nv.data.nameStory?.verdict ?? 'analyzed'})</p>`);
                    if (nv.data.nameStory?.headline) lines.push(`<p><strong>${esc(nv.data.nameStory.headline)}</strong></p>`);
                    if (nv.data.nameStory?.strategicAnalysis) lines.push(`<p>${esc(nv.data.nameStory.strategicAnalysis)}</p>`);
                  } else {
                    lines.push(`<h2>Name Validation</h2><p>Risk Score: ${nv.data.riskScore}/10 (${nv.data.riskLevel})</p>`);
                  }
                }
                const kw = intel.keywords;
                if (kw.status === 'done' && kw.data) {
                  const d = kw.data as Record<string, any>;
                  lines.push(`<h2>SEO Keywords</h2><p>Traffic Opportunity: ${d.totalOpportunity?.estimatedMonthlyTraffic?.toLocaleString() ?? 0}/mo</p>`);
                  if (d.quickWins?.length) {
                    lines.push('<table><tr><th>Quick Win Keyword</th><th>Volume</th></tr>' +
                      d.quickWins.slice(0, 5).map((k: any) => `<tr><td>${esc(k.term)}</td><td>${k.volumeEstimate}</td></tr>`).join('') + '</table>');
                  }
                }
                const comp = intel.competitors;
                if (comp.status === 'done' && comp.data) {
                  const d = comp.data as Record<string, any>;
                  if (d.competitors?.length) {
                    lines.push('<h2>Competitors</h2><table><tr><th>Name</th><th>Category</th><th>Weakness</th></tr>' +
                      d.competitors.slice(0, 5).map((c: any) => `<tr><td>${esc(c.name)}</td><td>${esc(c.category)}</td><td>${esc(c.detectedWeaknesses?.[0])}</td></tr>`).join('') + '</table>');
                  }
                }
                // Founder summary
                if (summary) lines.push(`<h2>Founder's Summary</h2><pre>${esc(summary)}</pre>`);
                // Create print overlay, add body class, print, cleanup
                const el = document.createElement('div');
                el.className = 'print-only';
                el.style.display = 'none';
                el.innerHTML = lines.join('');
                document.body.appendChild(el);
                document.body.classList.add('sf-printing');
                // Use requestAnimationFrame to stay within user gesture context
                requestAnimationFrame(() => {
                  window.print();
                  // Cleanup after print dialog closes
                  window.addEventListener('afterprint', function cleanup() {
                    document.body.removeChild(el);
                    document.body.classList.remove('sf-printing');
                    window.removeEventListener('afterprint', cleanup);
                  });
                });
              }}
            />

            {/* Daily mission card */}
            {startupData && validationPlanData && (
              <DailyMissionCard
                startup={startupData}
                validationPlanData={validationPlanData as Record<string, any>}
                onOpenChecklist={() => scrollTo('validationPlan')}
                onLogResults={() => scrollTo('mvpBlueprint')}
                onViewBlueprint={() => scrollTo('mvpBlueprint')}
              />
            )}

            {/* Stage lifecycle bar */}
            <StageLifecycleBar stage={startup.stage} />

            {/* Progress bar */}
            {runProgress && (
              <div className="card" style={{ padding: '12px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 13 }}>
                  <span className="text-white font-medium">
                    {runProgress.current < runProgress.total ? `Analyzing: ${runProgress.currentLabel}` : 'Intelligence analysis complete'}
                  </span>
                  <span className="text-gray-400">{runProgress.current}/{runProgress.total}</span>
                </div>
                <div style={{ height: 6, background: '#1e1e30', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${(runProgress.current / runProgress.total) * 100}%`, background: 'linear-gradient(90deg, #7c6ff7, #00d4ff)', borderRadius: 3, transition: 'width 0.5s ease' }} />
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                  {INTEL_TASKS.map(t => {
                    const s = intel[t.key].status;
                    return (
                      <span key={t.key} style={{ fontSize: 12, color: s === 'done' ? '#22c55e' : s === 'loading' ? '#f59e0b' : s === 'error' ? '#ef4444' : '#4b5563' }}>
                        {s === 'done' ? 'Done' : s === 'loading' ? '...' : s === 'error' ? 'Error' : '—'} {t.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Intel overview cards (idle/loading/done previews) */}
            {!runProgress && (() => {
              const allIdle = INTEL_TASKS.every(t => intel[t.key].status === 'idle');
              const doneTasks = INTEL_TASKS.filter(t => intel[t.key].status === 'done');
              const pendingTasks = INTEL_TASKS.filter(t => intel[t.key].status !== 'done');
              const totalCost = INTEL_TASKS.reduce((s, t) => s + t.cost, 0);

              if (allIdle) return (
                <div style={{
                  background: 'rgba(124,111,247,0.06)', border: '1px solid rgba(124,111,247,0.25)',
                  borderRadius: 14, padding: '28px 24px', textAlign: 'center',
                }}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}><BrainCircuit size={36} style={{ color: '#7c6ff7' }} /></div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: '#f0f0ff', marginBottom: 8 }}>Intelligence Analysis</h3>
                  <p style={{ fontSize: 13, color: '#8888aa', marginBottom: 16, lineHeight: 1.5 }}>
                    Run all 5 intelligence modules — name validation, keywords, competitors, trends, and sentiment analysis.
                  </p>
                  <p style={{ fontSize: 12, color: '#7c6ff7', marginBottom: 16 }}>Total cost: {totalCost} credits</p>
                  <button onClick={() => scrollTo('nameValidation')} style={{
                    background: 'linear-gradient(135deg, #7c6ff7, #00d4ff)', border: 'none',
                    borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 700,
                    padding: '10px 24px', cursor: 'pointer',
                  }}>→ Run Intelligence Analysis</button>
                </div>
              );

              return (
                <div>
                  {doneTasks.length > 0 && (
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" style={{ marginBottom: pendingTasks.length > 0 ? 12 : 0 }}>
                      {doneTasks.map(t => {
                        const s = intel[t.key];
                        return (
                          <button key={t.key} onClick={() => scrollTo(t.key)} style={{
                            background: 'rgba(34,197,94,0.04)', border: '1px solid #22c55e30', borderRadius: 10,
                            padding: '12px 14px', textAlign: 'left', cursor: 'pointer', minHeight: 88, width: '100%',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                              <span style={{ fontSize: 12, color: '#d1d5db', fontWeight: 600 }}>{t.label}</span>
                              <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#22c55e15', color: '#22c55e' }}>✓</span>
                            </div>
                            <p style={{ fontSize: 11, color: '#6b7280' }}>Complete</p>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {pendingTasks.length > 0 && (
                    <div style={{ fontSize: 12, color: '#8888aa', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span>Also available:</span>
                      {pendingTasks.map(t => (
                        <button key={t.key} onClick={() => scrollTo(t.key)} style={{
                          background: 'rgba(124,111,247,0.08)', border: '1px solid rgba(124,111,247,0.2)',
                          borderRadius: 6, padding: '3px 10px', fontSize: 11, color: '#7c6ff7',
                          cursor: 'pointer', fontWeight: 600,
                        }}>
                          {t.label} ({t.cost}cr)
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
            {!runProgress && !INTEL_TASKS.every(t => intel[t.key].status === 'idle') && false && (
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                {INTEL_TASKS.map(t => {
                  const s = intel[t.key];
                  const borderColor = s.status === 'done' ? '#22c55e30' : s.status === 'error' ? '#ef444430' : '#2a2a3e';
                  const bgColor = s.status === 'done' ? 'rgba(34,197,94,0.04)' : s.status === 'error' ? 'rgba(239,68,68,0.04)' : '#161627';
                  const statusColor = s.status === 'done' ? '#22c55e' : s.status === 'loading' ? '#f59e0b' : s.status === 'error' ? '#ef4444' : '#4b5563';
                  return (
                    <button
                      key={t.key}
                      onClick={() => scrollTo(t.key)}
                      style={{
                        background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 10,
                        padding: '12px 14px', textAlign: 'left', cursor: 'pointer', minHeight: 88,
                        width: '100%',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 12, color: s.status === 'done' ? '#d1d5db' : '#6b7280', fontWeight: 600 }}>{t.label}</span>
                        <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: s.status === 'done' ? '#22c55e15' : s.status === 'loading' ? '#f59e0b15' : s.status === 'error' ? '#ef444415' : '#1e1e30', color: statusColor }}>
                          {s.status === 'done' ? '✓' : s.status === 'loading' ? '…' : s.status === 'error' ? '!' : '–'}
                        </span>
                      </div>
                      {s.status === 'idle' && <p style={{ fontSize: 11, color: '#3a3a5e' }}>Not analyzed</p>}
                      {s.status === 'loading' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ height: 6, background: '#1e1e30', borderRadius: 3, animation: 'wizard-blob 2s ease-in-out infinite' }} />
                          <div style={{ height: 6, background: '#1e1e30', borderRadius: 3, animation: 'wizard-blob 2s ease-in-out infinite', width: '65%' }} />
                        </div>
                      )}
                      {s.status === 'error' && <p style={{ fontSize: 11, color: '#ef4444' }}>Failed — click retry</p>}
                      {s.status === 'done' && t.key === 'nameValidation' && intel.nameValidation.data && (() => {
                        const d = intel.nameValidation.data;
                        if (d.brandScore) {
                          const sc = d.brandScore.overall;
                          const c = sc >= 75 ? '#22c55e' : sc >= 50 ? '#f59e0b' : '#ef4444';
                          return (
                            <div>
                              <span style={{ fontSize: 20, fontWeight: 800, color: c }}>{sc}</span>
                              <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 4 }}>/ 100</span>
                              <p style={{ fontSize: 10, color: c, marginTop: 2 }}>{d.nameStory?.verdict ?? 'analyzed'}</p>
                            </div>
                          );
                        }
                        // Legacy mini-card
                        const c = d.riskLevel === 'low' ? '#22c55e' : d.riskLevel === 'medium' ? '#f59e0b' : '#ef4444';
                        return (
                          <div>
                            <span style={{ fontSize: 20, fontWeight: 800, color: c }}>{d.riskScore}</span>
                            <span style={{ fontSize: 11, color: c, marginLeft: 4 }}>{d.riskLevel} risk</span>
                          </div>
                        );
                      })()}
                      {s.status === 'done' && t.key === 'keywords' && intel.keywords.data && (() => {
                        const d = intel.keywords.data;
                        return (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {d.quickWins.slice(0, 3).map((kw, i) => (
                              <span key={i} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#7c6ff715', color: '#7c6ff7', border: '1px solid #7c6ff720' }}>
                                {kw.term.slice(0, 18)}
                              </span>
                            ))}
                            {d.quickWins.length > 3 && <span style={{ fontSize: 9, color: '#4b5563' }}>+{d.quickWins.length - 3}</span>}
                          </div>
                        );
                      })()}
                      {s.status === 'done' && t.key === 'competitors' && intel.competitors.data && (() => {
                        const d = intel.competitors.data;
                        const cc = d.marketOverview.crowdingLabel === 'uncrowded' ? '#22c55e' : d.marketOverview.crowdingLabel === 'moderate' ? '#f59e0b' : '#ef4444';
                        return (
                          <div>
                            <span style={{ fontSize: 20, fontWeight: 700, color: cc }}>{d.competitors.length}</span>
                            <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 4 }}>found</span>
                            <p style={{ fontSize: 10, color: cc, marginTop: 2, textTransform: 'capitalize' }}>{d.marketOverview.crowdingLabel}</p>
                          </div>
                        );
                      })()}
                      {s.status === 'done' && t.key === 'trends' && intel.trends.data && (() => {
                        const d = intel.trends.data;
                        const tc = d.timingAssessment.score >= 80 ? '#22c55e' : d.timingAssessment.score >= 60 ? '#f59e0b' : '#ef4444';
                        return (
                          <div>
                            {d.marketSize.value && <p style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>{d.marketSize.value}</p>}
                            {d.growthRate.value && <p style={{ fontSize: 11, color: '#22c55e' }}>↑ {d.growthRate.value}</p>}
                            <p style={{ fontSize: 10, color: tc, marginTop: 2 }}>{d.timingAssessment.verdict}</p>
                          </div>
                        );
                      })()}
                      {s.status === 'done' && t.key === 'sentiment' && intel.sentiment.data && (() => {
                        const d = intel.sentiment.data;
                        const sc = d.sentimentScore >= 50 ? '#22c55e' : d.sentimentScore >= 0 ? '#f59e0b' : '#ef4444';
                        return (
                          <div>
                            <span style={{ fontSize: 20, fontWeight: 700, color: sc }}>{d.sentimentScore >= 0 ? '+' : ''}{d.sentimentScore}</span>
                            <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 4 }}>{d.overallSentiment.replace('_', ' ')}</span>
                            {d.painPoints[0] && (
                              <p style={{ fontSize: 10, color: '#6b7280', marginTop: 3, fontStyle: 'italic' }}>
                                "{d.painPoints[0].point.slice(0, 32)}…"
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Your Next Move banner */}
            <NextMoveCard
              startup={startup}
              startupId={startupId!}
              pipeline={pipelineData}
              doneCount={doneCount}
              betaUserCount={dashboard.betaUsers?.length ?? 0}
              feedbackCount={feedbackAnalytics.totalResponses}
              validationTasksDone={Object.values(startupData?.validationProgress ?? {}).filter(Boolean).length}
              validationTasksTotal={validationPlanData ? ((validationPlanData as any).checklist?.length ?? 14) : 14}
              founderSummaryDone={summary.trim().length > 10}
              hasFinancials={baseScenario !== null}
              onRunIntelligence={runAllSequential}
              onScrollTo={scrollTo}
            />

            {/* Stage-appropriate metrics */}
            <StageMetrics
              startup={startup}
              metrics={metrics}
              feedbackAnalytics={feedbackAnalytics}
              pipeline={pipelineData}
              doneCount={doneCount}
              npsColor={npsColor}
              startupId={startupId}
            />

            {projections.length > 0 && (
              <div className="card">
                <h2 className="text-lg font-bold text-white mb-4">12-Month Revenue Projection (Base Case)</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={projections} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" />
                    <XAxis dataKey="month" tickFormatter={m => `M${m}`} tick={{ fill: '#6b7280', fontSize: 11 }} />
                    <YAxis tickFormatter={formatYAxis} tick={{ fill: '#6b7280', fontSize: 11 }} label={{ value: 'Monthly Revenue ($)', angle: -90, position: 'insideLeft', style: { fill: '#4b5563', fontSize: 11 }, offset: -5 }} />
                    <Tooltip content={({ active, payload, label }) => active && payload?.length ? (
                      <div className="bg-[#161627] border border-[#2a2a3e] rounded-lg p-3">
                        <p className="text-gray-400 text-xs mb-1">Month {label}</p>
                        {payload.map(p => <p key={String(p.dataKey)} style={{ color: p.color as string }} className="text-sm">{String(p.dataKey) === 'revenue' ? 'Revenue' : 'Net Profit'}: {formatCurrency(p.value as number)}</p>)}
                      </div>
                    ) : null} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value: string) => <span style={{ color: '#9ca3af', fontSize: 12 }}>{value === 'revenue' ? '— Revenue' : '··· Net Profit'}</span>}
                    />
                    <Line type="monotone" dataKey="revenue" name="revenue" stroke="#22c55e" strokeWidth={3} dot={false} />
                    <Line type="monotone" dataKey="netProfit" name="netProfit" stroke="#6366f1" strokeWidth={2} dot={false} strokeDasharray="4 4" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-6">
              {scenarioTable.length > 0 && (
                <div className="card">
                  <h2 className="text-lg font-bold text-white mb-4">Scenario Comparison</h2>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-[#2a2a3e]">
                      <th className="text-left py-2 text-gray-500 font-medium text-xs">Scenario</th>
                      <th className="text-right py-2 text-gray-500 font-medium text-xs">6m Rev</th>
                      <th className="text-right py-2 text-gray-500 font-medium text-xs">12m Rev</th>
                      <th className="text-right py-2 text-gray-500 font-medium text-xs">B/E</th>
                    </tr></thead>
                    <tbody>
                      {scenarioTable.map(s => (
                        <tr key={s.name} className="border-b border-[#2a2a3e]">
                          <td className="py-2 text-white">{s.name}</td>
                          <td className="py-2 text-right text-gray-300">{formatCurrency(s.rev6m, true)}</td>
                          <td className="py-2 text-right text-gray-300">{formatCurrency(s.rev12m, true)}</td>
                          <td className="py-2 text-right"><span className={s.breakEven ? 'text-green-400' : 'text-red-400'}>{s.breakEven ? `M${s.breakEven}` : '—'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="space-y-4">
                {topFeatureRequests.length > 0 && (
                  <div className="card">
                    <h2 className="text-base font-bold text-white mb-3">Top Feature Requests</h2>
                    {topFeatureRequests.slice(0, 3).map((fr, i) => (
                      <div key={fr.name} className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-300"><span className="text-gray-600 mr-2">#{i + 1}</span>{fr.name}</span>
                        <span className="text-brand-500 text-xs">{fr.count} req</span>
                      </div>
                    ))}
                  </div>
                )}
                {feedbackAnalytics.totalResponses > 0 && (
                  <div className="card">
                    <h2 className="text-base font-bold text-white mb-3">Recommendation Rate</h2>
                    <div className="flex items-center gap-4">
                      <ResponsiveContainer width={100} height={100}>
                        <PieChart><Pie data={recData} innerRadius={30} outerRadius={45} dataKey="value" startAngle={90} endAngle={-270}>
                          {recData.map((_, i) => <Cell key={i} fill={REC_COLORS[i]} />)}
                        </Pie></PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-1 text-sm">
                        <p className="text-green-400">✓ {feedbackAnalytics.recommendationRate.yes}% Would Recommend</p>
                        <p className="text-red-400">✗ {feedbackAnalytics.recommendationRate.no}% Would Not</p>
                        <p className="text-yellow-400">◌ {feedbackAnalytics.recommendationRate.maybe}% Maybe</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── DEEP ANALYSIS (PRO) ── */}
          <div ref={deepAnalysisRef} style={{ marginTop: 48 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <BrainCircuit size={18} className="text-brand-500" /> Deep Analysis
              {!isSubscribed && (
                <span className="badge-shimmer" style={{
                  fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 6,
                  color: '#f97316',
                }}>PRO</span>
              )}
            </h2>
            <FeatureGate
              feature="deep_analysis"
              title="Deep Analysis"
              description="Get a comprehensive 9-section analysis including market sizing, GTM strategy, risk matrix, and investor pitch framework."
            >
              <DeepAnalysis
                startupId={startupId!}
                deepAnalysis={(startupData?.deepAnalysis as any) ?? null}
                onGenerated={(data) => setStartupData(prev => prev ? { ...prev, deepAnalysis: data } : prev)}
              />
            </FeatureGate>
          </div>

          {/* ── VALIDATION & SIGNALS GRID ── */}
          <div ref={validationPlanRef} id="validation-section" style={{ marginTop: 48 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FlaskConical size={18} className="text-brand-500" /> Validation & Signals
              </h2>
              {startupData?.validationStartDate && (() => {
                const dayNum = Math.floor((Date.now() - new Date(startupData.validationStartDate).getTime()) / 86400000) + 1;
                return (
                  <span style={{
                    fontSize: 12, fontWeight: 700, padding: '4px 12px',
                    borderRadius: 8,
                    background: dayNum > 14 ? 'rgba(249,115,22,0.1)' : 'rgba(124,111,247,0.1)',
                    color: dayNum > 14 ? '#f97316' : '#7c6ff7',
                    border: `1px solid ${dayNum > 14 ? 'rgba(249,115,22,0.3)' : 'rgba(124,111,247,0.3)'}`,
                  }}>
                    {dayNum > 14 ? 'Day 14+ — Time to analyze your results!' : `Day ${Math.min(dayNum, 14)} of 14`}
                  </span>
                );
              })()}
            </div>
            <div className="dash-grid-2-1">
              <ValidationTracker
                startup={startupData ?? {}}
                validationPlanData={validationPlanData as Record<string, any> ?? null}
                onTaskToggle={handleTaskToggle}
                pipelineCreatedAt={pipelineData?.createdAt}
              />
              <SignalLog
                startup={{ ...(startupData ?? {}), id: startupId }}
                onSignalLogged={handleSignalLogged}
              />
            </div>
          </div>

          {/* ── MVP BLUEPRINT ── */}
          <div ref={mvpBlueprintRef} style={{ marginTop: 48 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Hammer size={18} className="text-brand-500" /> MVP Blueprint
            </h2>
            <MVPBlueprint
              startup={startupData ?? {}}
              onGenerateBlueprint={handleGenerateBlueprint}
              pipelineStatus={pipelineData?.status}
              pipelineStep={pipelineData?.currentStep}
            />
          </div>

          {/* ── BRAND INTELLIGENCE ── */}
          <div ref={nameRef} style={{ marginTop: 48 }}>
            {sectionHeader('nameValidation', 'name', 'Brand Intelligence')}
            {intel.nameValidation.status === 'idle' && (
              isPlaceholderName ? (
                <div className="card text-center py-12">
                  <p className="text-sm text-gray-500 mb-3">Rename required</p>
                  <p className="text-white font-semibold mb-1">Name your startup first</p>
                  <p className="text-gray-400 text-sm mb-4">Your startup still has a placeholder name. Give it a real name before running Brand Intelligence.</p>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', maxWidth: 400, margin: '0 auto' }}>
                    <input
                      type="text" placeholder="e.g. MenuCast" value={renamingTo}
                      onChange={e => setRenamingTo(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleRename()}
                      style={{ flex: 1, background: '#1a1a2e', border: '1px solid #333', borderRadius: 8, padding: '8px 12px', color: '#fff', fontSize: 14 }}
                    />
                    <button onClick={handleRename} disabled={isRenaming || renamingTo.trim().length < 2} className="btn-primary" style={{ whiteSpace: 'nowrap' }}>
                      {isRenaming ? 'Saving...' : 'Save & Analyze'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="card text-center py-12">
                  <p className="mb-3"><BrainCircuit size={36} style={{ color: '#7c6ff7' }} /></p>
                  <p className="text-white font-semibold mb-1">How strong is your brand?</p>
                  <p className="text-gray-400 text-sm mb-4">Get a full brand strategy analysis: naming score, messaging framework, personality, SEO, and more.</p>
                  <button onClick={() => runSection('nameValidation', 'name')} className="btn-primary">Analyze Brand</button>
                </div>
              )
            )}
            {intel.nameValidation.status === 'loading' && <div className="card"><IntelLoadingCinematic sectionKey="nameValidation" /></div>}
            {intel.nameValidation.status === 'error' && <div className="card text-center py-8"><p className="text-red-400 mb-3">{intel.nameValidation.error}</p><button onClick={() => runSection('nameValidation', 'name')} className="btn-secondary text-sm">Retry</button></div>}
            {intel.nameValidation.status === 'done' && intel.nameValidation.data && (() => {
              const d = intel.nameValidation.data;
              const pctx = extractPipelineContext(pipelineData);
              // Backward compat: detect old format
              const isLegacy = !d.brandScore && d.riskScore !== undefined;
              if (isLegacy) {
                return (
                  <div className="card section-enter">
                    <p className="text-gray-400 text-sm mb-2">This report was generated with an older version.</p>
                    <p className="text-white">Risk Score: <span className="font-bold">{d.riskScore}</span> ({d.riskLevel})</p>
                    <button onClick={() => runSection('nameValidation', 'name', true)} className="btn-primary mt-4 text-sm">Re-analyze with Brand Intelligence</button>
                  </div>
                );
              }
              const bs = d.brandScore;
              const verdictConfig = {
                strong: { label: 'Strong Brand', color: '#22c55e', bg: '#22c55e15' },
                promising: { label: 'Promising', color: '#f59e0b', bg: '#f59e0b15' },
                risky: { label: 'Needs Work', color: '#f97316', bg: '#f9731615' },
                rebrand: { label: 'Consider Rebrand', color: '#ef4444', bg: '#ef444415' },
              };
              const vc = verdictConfig[d.nameStory.verdict] || verdictConfig.promising;
              const cpText = (text: string) => { navigator.clipboard.writeText(text); };
              return (
                <div className="space-y-4 section-enter">
                  {/* Storytelling headline */}
                  <p style={{ fontSize: 20, fontWeight: 700, color: '#e5e7eb', lineHeight: 1.3 }}>
                    How well does &ldquo;{d.name}&rdquo; resonate with {pctx.audience ?? 'your target market'}?
                  </p>

                  {/* Hero: Brand Score + sub-scores + verdict (always visible) */}
                  <div className="card" style={{ display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
                    <BrandScoreRing score={bs.overall} />
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <h3 className="text-white font-bold text-2xl">"{d.name}"</h3>
                        <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: vc.bg, color: vc.color, border: `1px solid ${vc.color}40` }}>
                          {vc.label}
                        </span>
                      </div>
                      <SubScoreBar label="Availability" score={bs.availability} color="#00d4ff" />
                      <SubScoreBar label="Memorability" score={bs.memorability} color="#7c6ff7" />
                      <SubScoreBar label="Market Fit" score={bs.marketFit} color="#f97316" />
                      <SubScoreBar label="Growth Potential" score={bs.growthPotential} color="#22c55e" />
                    </div>
                  </div>

                  {/* Cross-section insight chip */}
                  {intel.keywords.status === 'done' && d.seoAnalysis?.competitiveness && (
                    <InsightChip text={`Brand SEO competitiveness: ${d.seoAnalysis.competitiveness}`} icon="" />
                  )}

                  {/* Panel 1: Strategic Analysis */}
                  <CollapsiblePanel title="Strategic Analysis" icon="" badge={d.nameStory.verdict}>
                    <h3 className="text-white font-bold text-lg mb-2">{d.nameStory.headline}</h3>
                    <p className="text-gray-300 text-sm leading-relaxed mb-3">{d.nameStory.strategicAnalysis}</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div style={{ padding: '10px 14px', borderRadius: 8, background: '#1a1a2e', border: '1px solid #2a2a3e' }}>
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">Phonetics</p>
                        <p className="text-gray-300 text-sm">{d.nameStory.phoneticsNote}</p>
                      </div>
                      <div style={{ padding: '10px 14px', borderRadius: 8, background: '#1a1a2e', border: '1px solid #2a2a3e' }}>
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">Emotional Response</p>
                        <p className="text-gray-300 text-sm">{d.nameStory.emotionalResponse}</p>
                      </div>
                    </div>
                  </CollapsiblePanel>

                  {/* Panel 2: Brand Personality & Messaging */}
                  <CollapsiblePanel title="Brand Personality & Messaging" icon="🎭">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                          <span style={{ padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: '#7c6ff720', color: '#7c6ff7', border: '1px solid #7c6ff740' }}>
                            {d.brandPersonality.archetype}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                          {d.brandPersonality.traits.map((t, i) => (
                            <span key={i} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: '#22c55e15', color: '#22c55e', border: '1px solid #22c55e30' }}>{t}</span>
                          ))}
                        </div>
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">Tone of Voice</p>
                        <p className="text-gray-300 text-sm mb-3">{d.brandPersonality.toneOfVoice}</p>
                        {d.brandPersonality.colorPalette.length > 0 && (
                          <>
                            <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-2">Color Palette</p>
                            <div style={{ display: 'flex', gap: 8 }}>
                              {d.brandPersonality.colorPalette.map((hex, i) => (
                                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                  <div style={{ width: 36, height: 36, borderRadius: 8, background: hex, border: '2px solid #2a2a3e', cursor: 'pointer' }} onClick={() => cpText(hex)} title={`Click to copy ${hex}`} />
                                  <span className="text-gray-500" style={{ fontSize: 9, fontFamily: 'monospace' }}>{hex}</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-2">Taglines</p>
                        <div className="space-y-2 mb-4">
                          {d.messagingFramework.taglines.map((tag, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: 8, background: '#1a1a2e', border: '1px solid #2a2a3e', cursor: 'pointer' }} onClick={() => cpText(tag)} title="Click to copy">
                              <span className="text-white text-sm font-medium">"{tag}"</span>
                              <span style={{ fontSize: 10, color: '#7c6ff7', flexShrink: 0, marginLeft: 8 }}>Copy</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">Elevator Pitch</p>
                        <div style={{ padding: '10px 14px', borderRadius: 8, background: '#1a1a2e', border: '1px solid #2a2a3e', marginBottom: 12, cursor: 'pointer' }} onClick={() => cpText(d.messagingFramework.elevatorPitch)} title="Click to copy">
                          <p className="text-gray-300 text-sm italic">"{d.messagingFramework.elevatorPitch}"</p>
                        </div>
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">Positioning Statement</p>
                        <p className="text-gray-400 text-xs leading-relaxed">{d.messagingFramework.positioningStatement}</p>
                      </div>
                    </div>
                  </CollapsiblePanel>

                  {/* Panel 3: Domain & Legal */}
                  <CollapsiblePanel title="Domain & Legal" icon="" badge={`${d.legalAnalysis.trademarkRisk} risk`}>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <div style={{ padding: '10px 14px', borderRadius: 8, background: '#1a1a2e', border: '1px solid #2a2a3e', marginBottom: 12 }}>
                          <p className="text-white font-mono text-sm font-bold">{d.domainStrategy.primary}</p>
                          <p className="text-gray-400 text-xs mt-1">{d.domainStrategy.verdict}</p>
                          {d.domainStrategy.estimatedCost && <p className="text-gray-500 text-xs mt-1">Est. cost: {d.domainStrategy.estimatedCost}</p>}
                        </div>
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-2">Alternatives</p>
                        <div className="space-y-1">
                          {d.domainStrategy.alternatives.map((alt, i) => (
                            <div key={i} style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, background: '#0d0d1f' }}>
                              <span className="text-white font-mono">{alt}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        {(() => {
                          const trColor = d.legalAnalysis.trademarkRisk === 'high' ? '#ef4444' : d.legalAnalysis.trademarkRisk === 'medium' ? '#f59e0b' : '#22c55e';
                          return (
                            <>
                              <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: `${trColor}15`, color: trColor, border: `1px solid ${trColor}40`, marginBottom: 12 }}>
                                {d.legalAnalysis.trademarkRisk.toUpperCase()} RISK
                              </div>
                              {d.legalAnalysis.conflicts.length > 0 && (
                                <div className="mb-3">
                                  {d.legalAnalysis.conflicts.map((c, i) => <p key={i} className="text-gray-300 text-xs mb-1">• {c}</p>)}
                                </div>
                              )}
                              <p className="text-gray-400 text-xs mb-3">{d.legalAnalysis.recommendation}</p>
                              {d.legalAnalysis.jurisdictions.length > 0 && (
                                <div>
                                  <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">Register in</p>
                                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {d.legalAnalysis.jurisdictions.map((j, i) => (
                                      <span key={i} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#1a1a2e', color: '#9ca3af', border: '1px solid #2a2a3e' }}>{j}</span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {d.trademarkMentions.length > 0 && (
                                <div className="mt-3"><p className="text-yellow-400 text-xs font-medium mb-1">Trademark mentions found:</p>
                                  {d.trademarkMentions.map((tm, i) => <a key={i} href={tm.url} target="_blank" rel="noopener noreferrer" className="text-brand-500 text-xs hover:underline block">{tm.title.slice(0, 60)}</a>)}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </CollapsiblePanel>

                  {/* Panel 4: Market Presence & SEO */}
                  <CollapsiblePanel title="Market Presence & SEO" icon="" badge={`${d.marketPresence.differentiationScore}/100`}>
                    {(d.marketPresence.directConflicts.length > 0 || d.marketPresence.similarNames.length > 0) && (
                      <>
                        {d.marketPresence.directConflicts.length > 0 && (
                          <div className="overflow-x-auto mb-3">
                            <table className="w-full text-xs">
                              <thead><tr className="border-b border-[#2a2a3e]">
                                <th className="text-left py-2 px-2 text-gray-500 font-medium">Name</th>
                                <th className="text-left py-2 px-2 text-gray-500 font-medium">Threat</th>
                                <th className="text-left py-2 px-2 text-gray-500 font-medium">Note</th>
                              </tr></thead>
                              <tbody>
                                {d.marketPresence.directConflicts.map((c, i) => {
                                  const tc = c.threat === 'high' ? '#ef4444' : c.threat === 'medium' ? '#f59e0b' : '#22c55e';
                                  return (
                                    <tr key={i} className="border-b border-[#2a2a3e] hover:bg-[#1a1a2e]">
                                      <td className="py-2 px-2">
                                        {c.url ? <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-brand-500 hover:underline">{c.name}</a> : <span className="text-white">{c.name}</span>}
                                      </td>
                                      <td className="py-2 px-2"><span style={{ color: tc, fontSize: 10, padding: '1px 6px', background: `${tc}20`, borderRadius: 4 }}>{c.threat}</span></td>
                                      <td className="py-2 px-2 text-gray-400">{c.note}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {d.marketPresence.similarNames.length > 0 && (
                          <div className="mb-4">
                            <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-2">Similar Names in Market</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {d.marketPresence.similarNames.map((n, i) => (
                                <span key={i} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: '#f59e0b15', color: '#f59e0b', border: '1px solid #f59e0b30' }}>{n}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3">
                      {[
                        { label: 'Search Volume', value: d.seoAnalysis.searchVolume },
                        { label: 'Competitiveness', value: d.seoAnalysis.competitiveness },
                        { label: 'Keyword Synergy', value: d.seoAnalysis.keywordSynergy },
                        { label: 'Recommendation', value: d.seoAnalysis.recommendation },
                      ].map((item, i) => (
                        <div key={i} style={{ padding: '10px 14px', borderRadius: 8, background: '#1a1a2e', border: '1px solid #2a2a3e' }}>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">{item.label}</p>
                          <p className="text-gray-300 text-sm">{item.value}</p>
                        </div>
                      ))}
                    </div>
                  </CollapsiblePanel>

                  {/* Panel 5: Alternatives & Action Plan */}
                  <CollapsiblePanel title="Alternatives & Action Plan" icon="" badge={Array.isArray(d.brandAlternatives) ? `${d.brandAlternatives.length} alternatives` : undefined}>
                    {Array.isArray(d.brandAlternatives) && d.brandAlternatives.length > 0 && (
                      <div className="mb-4">
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-3">Brand Alternatives</p>
                        <div className="grid sm:grid-cols-2 gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                          {(d.brandAlternatives as Array<{ name: string; reasoning: string; style: string; availableDomain: string; score: number }>).map((alt, i) => (
                            <div key={i} style={{ padding: '14px 16px', borderRadius: 10, background: '#1a1a2e', border: '1px solid #2a2a3e' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                <span className="text-white font-bold">{alt.name}</span>
                                <span style={{ fontSize: 13, fontWeight: 800, color: alt.score >= 70 ? '#22c55e' : alt.score >= 50 ? '#f59e0b' : '#ef4444' }}>{alt.score}</span>
                              </div>
                              <p className="text-gray-400 text-xs mb-2">{alt.reasoning}</p>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#7c6ff715', color: '#7c6ff7' }}>{alt.style}</span>
                                <span className="text-gray-500 font-mono" style={{ fontSize: 10 }}>{alt.availableDomain}</span>
                              </div>
                              <div style={{ height: 4, background: '#0d0d1f', borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${alt.score}%`, background: alt.score >= 70 ? '#22c55e' : alt.score >= 50 ? '#f59e0b' : '#ef4444', borderRadius: 2, transition: 'width 0.8s ease' }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-3">Action Plan</p>
                    <div className="grid sm:grid-cols-3 gap-4">
                      {[
                        { phase: 'This Week', items: d.actionPlan.thisWeek, color: '#f97316' },
                        { phase: 'This Month', items: d.actionPlan.thisMonth, color: '#7c6ff7' },
                        { phase: 'Before Launch', items: d.actionPlan.beforeLaunch, color: '#22c55e' },
                      ].map((phase, pi) => (
                        <div key={pi}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: phase.color }} />
                            <span style={{ fontSize: 13, fontWeight: 700, color: phase.color }}>{phase.phase}</span>
                          </div>
                          {phase.items.map((item, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                              <span style={{ color: phase.color, fontWeight: 700, fontSize: 12, marginTop: 1, flexShrink: 0 }}>{i + 1}.</span>
                              <p className="text-gray-300 text-xs">{item}</p>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                    {d.recentNews.length > 0 && (
                      <div className="mt-4 pt-3" style={{ borderTop: '1px solid #2a2a3e' }}>
                        <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-2">Recent News Mentions</p>
                        {d.recentNews.map((n, i) => <a key={i} href={n.url} target="_blank" rel="noopener noreferrer" className="text-brand-500 text-xs hover:underline block">{n.title.slice(0, 60)} <span className="text-gray-600">({n.age})</span></a>)}
                      </div>
                    )}
                  </CollapsiblePanel>
                </div>
              );
            })()}
          </div>

          {/* ── KEYWORDS ── */}
          <div ref={keywordsRef} style={{ marginTop: 48 }}>
            {sectionHeader('keywords', 'keywords', 'Keyword Intelligence')}
            {intel.keywords.status === 'idle' && (
              <div className="card text-center py-12">
                <p className="mb-3"><Key size={36} style={{ color: '#7c6ff7' }} /></p>
                <p className="text-white font-semibold mb-1">Find your best keywords</p>
                <p className="text-gray-400 text-sm mb-4">Discover high-opportunity keywords, SEO strategy, and content calendar.</p>
                <button onClick={() => runSection('keywords', 'keywords')} className="btn-primary">Find Keywords</button>
              </div>
            )}
            {intel.keywords.status === 'loading' && <div className="card"><IntelLoadingCinematic sectionKey="keywords" /></div>}
            {intel.keywords.status === 'error' && <div className="card text-center py-8"><p className="text-red-400 mb-3">{intel.keywords.error}</p><button onClick={() => runSection('keywords', 'keywords')} className="btn-secondary text-sm">Retry</button></div>}
            {intel.keywords.status === 'done' && intel.keywords.data && (() => {
              const d = intel.keywords.data;
              const pctx = extractPipelineContext(pipelineData);
              return (
                <div className="space-y-4 section-enter">
                  {/* Storytelling headline */}
                  <p style={{ fontSize: 20, fontWeight: 700, color: '#e5e7eb', lineHeight: 1.3 }}>
                    What {pctx.audience ?? 'your customers'} search for to solve {pctx.problem ?? 'their problem'}
                  </p>

                  {/* Hero: 3 KPI cards */}
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="card section-enter-d1 text-center"><p className="text-gray-500 text-xs mb-1">Est. Monthly Traffic</p><p className="text-2xl font-bold text-brand-500">{d.totalOpportunity.estimatedMonthlyTraffic.toLocaleString()}</p><p className="text-gray-500 text-xs mt-1">if you rank for quick wins</p></div>
                    <div className="card section-enter-d1 text-center"><p className="text-gray-500 text-xs mb-1">Est. Monthly Leads</p><p className="text-2xl font-bold text-green-400">{d.totalOpportunity.estimatedMonthlyLeads.toLocaleString()}</p><p className="text-gray-500 text-xs mt-1">at {d.totalOpportunity.conversionAssumption} conversion</p></div>
                    <div className="card section-enter-d1 text-center"><p className="text-gray-500 text-xs mb-1">Quick Wins</p><p className="text-2xl font-bold text-yellow-400">{d.quickWins.length}</p><p className="text-gray-500 text-xs mt-1">low-competition targets</p></div>
                  </div>

                  {/* Cross-section insight chip */}
                  {intel.nameValidation.status === 'done' && intel.nameValidation.data?.seoAnalysis?.keywordSynergy && (
                    <InsightChip text={`Keyword synergy: ${intel.nameValidation.data.seoAnalysis.keywordSynergy}`} icon="" />
                  )}

                  {/* Panel 1: Quick Win Keywords */}
                  {d.quickWins.length > 0 && (
                    <CollapsiblePanel title="Quick Win Keywords" icon="" defaultOpen badge={`${d.quickWins.length} targets`}>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        {d.quickWins.slice(0, 6).map((kw, i) => (
                          <div key={i} style={{ padding: '8px 12px', borderRadius: 8, background: '#1e1e30', border: '1px solid #2a2a3e', minWidth: 140 }}>
                            <p className="text-white text-sm font-medium">{kw.term}</p>
                            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                              <span style={{ fontSize: 10, color: '#22c55e', background: '#22c55e15', borderRadius: 3, padding: '1px 5px' }}>{kw.difficulty}</span>
                              <span style={{ fontSize: 10, color: '#f59e0b', background: '#f59e0b15', borderRadius: 3, padding: '1px 5px' }}>{kw.volumeEstimate} vol</span>
                            </div>
                            <p className="text-gray-500 text-xs mt-1">{kw.actionableInsight}</p>
                          </div>
                        ))}
                      </div>
                    </CollapsiblePanel>
                  )}

                  {/* Panel 2: Full Keyword Table */}
                  <CollapsiblePanel title="Full Keyword Table" icon="" badge={`${kwData.length} keywords`}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {['all', 'primary', 'secondary', 'long_tail'].map(cat => {
                          const count = cat === 'all' ? kwData.length : kwData.filter(k => k.category === cat).length;
                          return (
                            <button key={cat} onClick={() => setKwFilter(cat)} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, border: `1px solid ${kwFilter === cat ? '#7c6ff7' : '#2a2a3e'}`, background: kwFilter === cat ? '#7c6ff715' : 'transparent', color: kwFilter === cat ? '#7c6ff7' : '#9ca3af', cursor: 'pointer' }}>
                              {cat === 'long_tail' ? 'Long Tail' : cat.charAt(0).toUpperCase() + cat.slice(1)} ({count})
                            </button>
                          );
                        })}
                      </div>
                      <span className="text-gray-500 text-xs">{filteredKw.length} keywords</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="border-b border-[#2a2a3e]">
                          {[['term', 'Keyword'], ['volumeEstimate', 'Volume'], ['difficulty', 'Difficulty'], ['intent', 'Intent'], ['weightedScore', 'Score']].map(([col, lbl]) => (
                            <th key={col} onClick={() => setKwSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }))} className="text-left py-2 px-2 text-gray-500 font-medium cursor-pointer hover:text-white select-none">
                              {lbl} {kwSort.col === col ? (kwSort.dir === 'asc' ? '↑' : '↓') : ''}
                            </th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {sortedKw.slice(0, 25).map((kw, i) => (
                            <tr key={i} className="border-b border-[#2a2a3e] hover:bg-[#1a1a2e]">
                              <td className="py-2 px-2 text-white font-medium">{kw.term}</td>
                              <td className="py-2 px-2"><span style={{ color: kw.volumeEstimate === 'high' ? '#ef4444' : kw.volumeEstimate === 'medium' ? '#f59e0b' : '#22c55e' }}>{kw.volumeEstimate}</span></td>
                              <td className="py-2 px-2"><span style={{ color: kw.difficulty === 'hard' ? '#ef4444' : kw.difficulty === 'medium' ? '#f59e0b' : '#22c55e' }}>{kw.difficulty}</span></td>
                              <td className="py-2 px-2 text-gray-400">{kw.intent}</td>
                              <td className="py-2 px-2">
                                {(() => {
                                  const ws = computeWeightedScore(kw);
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <div style={{ width: 40, height: 4, background: '#1e1e30', borderRadius: 2, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', width: `${ws * 10}%`, background: ws >= 7 ? '#22c55e' : ws >= 4 ? '#f59e0b' : '#ef4444', borderRadius: 2 }} />
                                      </div>
                                      <span className="text-white">{ws}/10</span>
                                    </div>
                                  );
                                })()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CollapsiblePanel>

                  {/* Panel 3: SEO Roadmap & Content Calendar */}
                  <CollapsiblePanel title="SEO Roadmap & Content Calendar" icon="">
                    <div className="space-y-3 mb-4">
                      {([['Phase 1', 'Weeks 1-4', d.seoStrategy.phase1, '#22c55e'], ['Phase 2', 'Weeks 5-8', d.seoStrategy.phase2, '#7c6ff7'], ['Phase 3', 'Weeks 9-12', d.seoStrategy.phase3, '#00d4ff']] as const).map(([phase, timeline, text, color]) => (
                        <div key={phase} style={{ background: `${color}08`, border: `1px solid ${color}20`, borderRadius: 10, padding: '12px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color, background: `${color}20`, borderRadius: 4, padding: '2px 8px' }}>{phase}</span>
                            <span style={{ fontSize: 10, color: '#6b7280' }}>{timeline}</span>
                          </div>
                          <p className="text-gray-300 text-sm" style={{ lineHeight: 1.5 }}>{text}</p>
                        </div>
                      ))}
                      <p className="text-gray-500 text-xs mt-1">⏱ {d.seoStrategy.estimatedTimeToRank}</p>
                    </div>
                    {d.contentCalendar.length > 0 && <ContentCalendarCard calendar={d.contentCalendar} />}
                  </CollapsiblePanel>
                </div>
              );
            })()}
          </div>

          {/* ── COMPETITORS ── */}
          <div ref={competitorsRef} style={{ marginTop: 48 }}>
            {sectionHeader('competitors', 'competitors', 'Competitor Intelligence')}
            {intel.competitors.status === 'idle' && (
              <div className="card text-center py-12">
                <p className="mb-3"><Swords size={36} style={{ color: '#7c6ff7' }} /></p>
                <p className="text-white font-semibold mb-1">Know your competition</p>
                <p className="text-gray-400 text-sm mb-4">Analyze competitor strengths, weaknesses, pricing, battle cards, and strategic advice.</p>
                <button onClick={() => runSection('competitors', 'competitors')} className="btn-primary">Analyze Competitors</button>
              </div>
            )}
            {intel.competitors.status === 'loading' && <div className="card"><IntelLoadingCinematic sectionKey="competitors" /></div>}
            {intel.competitors.status === 'error' && <div className="card text-center py-8"><p className="text-red-400 mb-3">{intel.competitors.error}</p><button onClick={() => runSection('competitors', 'competitors')} className="btn-secondary text-sm">Retry</button></div>}
            {intel.competitors.status === 'done' && intel.competitors.data && (() => {
              const d = intel.competitors.data;
              const pctx = extractPipelineContext(pipelineData);
              const mo = d.marketOverview;
              const crowdColor = mo.crowdingLabel === 'uncrowded' ? '#22c55e' : mo.crowdingLabel === 'moderate' ? '#f59e0b' : mo.crowdingLabel === 'crowded' ? '#f97316' : '#ef4444';
              return (
                <div className="space-y-4 section-enter">
                  {/* Storytelling headline */}
                  <p style={{ fontSize: 20, fontWeight: 700, color: '#e5e7eb', lineHeight: 1.3 }}>
                    Who&rsquo;s competing for {pctx.audience ?? 'your market'}&rsquo;s attention
                  </p>

                  {/* Hero: Key metrics always visible */}
                  <div className="card section-enter-d1">
                    <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 160 }}>
                        <p className="text-gray-500 text-xs mb-2">Market Crowding</p>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 36, fontWeight: 700, color: crowdColor }}>{d.competitors.length}</span>
                          <span className="text-gray-400 text-sm">competitors</span>
                        </div>
                        <div style={{ height: 8, background: '#1e1e30', borderRadius: 4, overflow: 'hidden', marginBottom: 4 }}>
                          <div style={{ height: '100%', width: `${mo.crowdingScore}%`, background: crowdColor, borderRadius: 4, transition: 'width 1s ease' }} />
                        </div>
                        <span style={{ fontSize: 11, color: crowdColor, background: `${crowdColor}20`, padding: '2px 8px', borderRadius: 4, display: 'inline-block', textTransform: 'capitalize' }}>{mo.crowdingLabel}</span>
                        <div className="space-y-1 mt-3">
                          <p className="text-gray-500 text-xs">Dominant model: <span className="text-white capitalize">{mo.dominantModel}</span></p>
                          <p className="text-gray-500 text-xs">Funding activity: <span style={{ color: mo.fundingActivity === 'high' ? '#ef4444' : mo.fundingActivity === 'moderate' ? '#f59e0b' : '#9ca3af', textTransform: 'capitalize' }}>{mo.fundingActivity}</span></p>
                          {mo.priceRange.min && <p className="text-gray-500 text-xs">Detected prices from: <span className="text-white">{mo.priceRange.min}</span></p>}
                        </div>
                      </div>
                      <div style={{ flex: 1, minWidth: 140 }}>
                        <p className="text-gray-500 text-xs mb-3">Threat Distribution</p>
                        {(['high', 'medium', 'low'] as const).map(level => {
                          const color = level === 'high' ? '#ef4444' : level === 'medium' ? '#f59e0b' : '#22c55e';
                          const count = d.competitors.filter(c => c.threatLevel === level).length;
                          return (
                            <div key={level} style={{ marginBottom: 8 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                                <span style={{ color, textTransform: 'capitalize' }}>{level} threat</span>
                                <span className="text-gray-400">{count}</span>
                              </div>
                              <div style={{ height: 5, background: '#1e1e30', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${d.competitors.length > 0 ? (count / d.competitors.length) * 100 : 0}%`, background: color, borderRadius: 3 }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Cross-section insight chip */}
                  {intel.keywords.status === 'done' && (
                    <InsightChip text={`${d.competitors.length} competitors identified in a ${mo.crowdingLabel} market`} icon="" />
                  )}

                  {/* Panel 1: Weakness Radar */}
                  <CollapsiblePanel title="Weakness Radar" icon="">
                    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <WeaknessPentagon map={d.competitorWeaknessMap} />
                    </div>
                  </CollapsiblePanel>

                  {/* Panel 2: Battle Cards */}
                  {d.battleCards.length > 0 && (
                    <CollapsiblePanel title="Battle Cards" icon="" badge={`${d.battleCards.length} cards`}>
                      {d.battleCards.length > 1 && (
                        <p style={{ fontSize: 11, color: '#6b7280', marginBottom: 6 }}>← Scroll to see {d.battleCards.length} cards →</p>
                      )}
                      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 12, scrollSnapType: 'x mandatory' }}>
                        {d.battleCards.map((bc, i) => (
                          <div key={i} style={{ minWidth: 300, maxWidth: 320, background: '#161627', border: '1px solid #2a2a3e', borderRadius: 12, padding: '16px', flexShrink: 0, scrollSnapAlign: 'start' }}>
                            <p className="text-white font-bold text-sm mb-3">vs. {bc.competitor.slice(0, 30)}</p>
                            <div className="space-y-3">
                              <div>
                                <p style={{ fontSize: 10, color: '#ef4444', fontWeight: 700, marginBottom: 2 }}>THEY WIN WHEN</p>
                                <p className="text-gray-400 text-xs">{bc.theyWinWhen}</p>
                              </div>
                              <div>
                                <p style={{ fontSize: 10, color: '#22c55e', fontWeight: 700, marginBottom: 2 }}>YOU WIN WHEN</p>
                                <p className="text-gray-400 text-xs">{bc.youWinWhen}</p>
                              </div>
                              <div style={{ background: '#7c6ff715', borderRadius: 6, padding: '8px 10px' }}>
                                <p style={{ fontSize: 10, color: '#7c6ff7', fontWeight: 700, marginBottom: 4 }}>SALES SCRIPT</p>
                                <p className="text-gray-300 text-xs italic">{bc.salesScript}</p>
                                <button onClick={() => copyText(bc.salesScript, 100 + i)} style={{ marginTop: 6, fontSize: 10, color: '#7c6ff7', background: 'none', border: '1px solid #7c6ff730', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>
                                  {copiedIdx === 100 + i ? 'Copied' : 'Copy'}
                                </button>
                              </div>
                              <div style={{ background: '#1e1e30', borderRadius: 6, padding: '8px 10px' }}>
                                <p style={{ fontSize: 10, color: '#f59e0b', fontWeight: 700, marginBottom: 2 }}>OBJECTION</p>
                                <p className="text-gray-400 text-xs italic mb-2">{bc.objection}</p>
                                <p style={{ fontSize: 10, color: '#22c55e', fontWeight: 700, marginBottom: 2 }}>RESPONSE</p>
                                <p className="text-gray-400 text-xs italic">{bc.objectionResponse}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsiblePanel>
                  )}

                  {/* Panel 3: Competitor Profiles */}
                  {d.competitors.length > 0 && (
                    <CollapsiblePanel title="Competitor Profiles" icon="🏢" badge={`${d.competitors.length} companies`}>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead><tr className="border-b border-[#2a2a3e]">
                            <th className="text-left py-2 px-3 text-gray-500 font-medium">Company</th>
                            <th className="text-left py-2 px-3 text-gray-500 font-medium">Threat</th>
                            <th className="text-left py-2 px-3 text-gray-500 font-medium">Model</th>
                            <th className="text-left py-2 px-3 text-gray-500 font-medium">Customers</th>
                            <th className="text-left py-2 px-3 text-gray-500 font-medium">Funding</th>
                            <th className="text-left py-2 px-3 text-gray-500 font-medium w-8"></th>
                          </tr></thead>
                          <tbody>
                            {d.competitors.map((c, i) => {
                              const tc = c.threatLevel === 'high' ? '#ef4444' : c.threatLevel === 'medium' ? '#f59e0b' : '#22c55e';
                              const isExp = expandedCompetitor === i;
                              return (
                                <>
                                  <tr key={`r-${i}`} className="border-b border-[#2a2a3e] hover:bg-[#1a1a2e] cursor-pointer" onClick={() => setExpandedCompetitor(isExp ? null : i)}>
                                    <td className="py-2 px-3">
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                        <img src={`https://www.google.com/s2/favicons?domain=${c.domain}&sz=16`} width={16} height={16} alt="" style={{ borderRadius: 3, flexShrink: 0 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                        <div>
                                          <a href={`https://${c.domain}`} target="_blank" rel="noopener noreferrer" className="text-white hover:text-brand-500" onClick={e => e.stopPropagation()}>{c.name.slice(0, 35)}</a>
                                          <p style={{ fontSize: 10, color: '#4b5563' }}>{c.domain}</p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="py-2 px-3"><span style={{ color: tc, fontSize: 10, padding: '1px 6px', background: `${tc}20`, borderRadius: 4, textTransform: 'capitalize' }}>{c.threatLevel}</span></td>
                                    <td className="py-2 px-3 text-gray-400">{c.pricing.model}{c.pricing.detectedPrice ? ` ${c.pricing.detectedPrice}` : ''}</td>
                                    <td className="py-2 px-3 text-gray-400">{c.targetCustomer}</td>
                                    <td className="py-2 px-3"><span className={c.fundingDetected ? 'text-green-400' : 'text-gray-600'}>{c.fundingDetected ?? '—'}</span></td>
                                    <td className="py-2 px-3 text-gray-500 text-center">{isExp ? '▲' : '▼'}</td>
                                  </tr>
                                  {isExp && (
                                    <tr key={`e-${i}`} className="border-b border-[#2a2a3e]">
                                      <td colSpan={6} style={{ padding: '12px 16px', background: '#1a1a2e' }}>
                                        <p className="text-gray-300 text-xs mb-3">{c.description}</p>
                                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
                                          {c.detectedStrengths.length > 0 && (
                                            <div>
                                              <p style={{ fontSize: 10, color: '#22c55e', fontWeight: 700, marginBottom: 4 }}>STRENGTHS</p>
                                              <div style={{ display: 'flex', gap: 4 }}>
                                                {c.detectedStrengths.map((s, j) => <span key={j} style={{ fontSize: 10, padding: '2px 6px', background: '#22c55e15', color: '#22c55e', borderRadius: 4 }}>{s}</span>)}
                                              </div>
                                            </div>
                                          )}
                                          {c.detectedWeaknesses.length > 0 && (
                                            <div>
                                              <p style={{ fontSize: 10, color: '#ef4444', fontWeight: 700, marginBottom: 4 }}>WEAKNESSES</p>
                                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                                {c.detectedWeaknesses.map((w, j) => <span key={j} style={{ fontSize: 10, padding: '2px 6px', background: '#ef444415', color: '#ef4444', borderRadius: 4 }}>{w}</span>)}
                                              </div>
                                            </div>
                                          )}
                                          {c.geography && <div><p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, marginBottom: 4 }}>GEO</p><span style={{ fontSize: 10, color: '#9ca3af' }}>{c.geography}</span></div>}
                                          {c.estimatedAge && <div><p style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, marginBottom: 4 }}>AGE</p><span style={{ fontSize: 10, color: '#9ca3af' }}>{c.estimatedAge}</span></div>}
                                        </div>
                                        {c.howToBeat && (
                                          <div style={{ padding: '8px 10px', borderRadius: 6, background: '#22c55e10', border: '1px solid #22c55e20' }}>
                                            <p style={{ fontSize: 10, color: '#22c55e', fontWeight: 700, marginBottom: 2 }}>HOW TO BEAT THEM</p>
                                            <p className="text-gray-300 text-xs">{c.howToBeat}</p>
                                          </div>
                                        )}
                                        {c.recentNews && <p className="text-gray-600 text-xs mt-2">📰 {c.recentNews}</p>}
                                      </td>
                                    </tr>
                                  )}
                                </>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CollapsiblePanel>
                  )}

                  {/* Panel 4: Market Gaps & Positioning */}
                  <CollapsiblePanel title="Market Gaps & Positioning" icon="" badge={d.marketGaps.length > 0 ? `${d.marketGaps.length} gaps` : undefined}>
                    {d.marketGaps.length > 0 && (
                      <div className="mb-4">
                        <div className="grid sm:grid-cols-2 gap-3">
                          {d.marketGaps.map((gap, i) => {
                            const gapObj = typeof gap === 'string' ? { gap: gap, opportunity: '', complaintCount: 0 } : gap;
                            const gapText = gapObj.gap || (gapObj as any).description || (gapObj as any).signal || (gapObj as any).text || '';
                            const oppText = gapObj.opportunity || (gapObj as any).action || (gapObj as any).recommendation || '';
                            const count = gapObj.complaintCount ?? (gapObj as any).count ?? 0;
                            if (!gapText && !oppText) return null;
                            return (
                              <div key={i} style={{ padding: '14px 16px', borderRadius: 10, background: '#22c55e08', border: '1px solid #22c55e30' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                  <p className="text-red-400 text-xs font-bold" style={{ flex: 1 }}>{gapText || 'Market gap identified'}</p>
                                  {count > 0 && <span style={{ fontSize: 10, padding: '1px 6px', background: '#ef444415', color: '#ef4444', borderRadius: 4, flexShrink: 0, marginLeft: 8 }}>{count} signal{count !== 1 ? 's' : ''}</span>}
                                </div>
                                {oppText && <p className="text-green-400 text-xs">{oppText}</p>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div style={{ padding: '12px 14px', borderRadius: 8, background: '#7c6ff715', border: '1px solid #7c6ff730', marginBottom: 12 }}>
                      <p style={{ fontSize: 11, color: '#7c6ff7', fontWeight: 700, marginBottom: 6 }}>YOUR POSITIONING STATEMENT</p>
                      <p className="text-gray-200 text-sm">{d.positioningRecommendation.statement}</p>
                      <button onClick={() => copyText(d.positioningRecommendation.statement, 200)} style={{ marginTop: 8, fontSize: 10, color: '#7c6ff7', background: 'none', border: '1px solid #7c6ff730', borderRadius: 4, padding: '2px 8px', cursor: 'pointer' }}>
                        {copiedIdx === 200 ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, marginBottom: 8 }}>TAGLINE OPTIONS</p>
                    <div className="space-y-2 mb-4">
                      {d.positioningRecommendation.taglines.map((tag, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, background: '#1e1e30' }}>
                          <div style={{ flex: 1 }}>
                            <p className="text-white text-sm italic">"{tag.text}"</p>
                            <p style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{tag.angle}</p>
                          </div>
                          <button onClick={() => copyText(tag.text, 210 + i)} style={{ fontSize: 10, color: '#9ca3af', background: 'none', border: '1px solid #2a2a3e', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', flexShrink: 0 }}>
                            {copiedIdx === 210 + i ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                      ))}
                    </div>
                    <div style={{ padding: '10px 12px', borderRadius: 8, background: '#ef444410', border: '1px solid #ef444430' }}>
                      <p style={{ fontSize: 11, color: '#ef4444', fontWeight: 700, marginBottom: 4 }}>WHAT TO AVOID</p>
                      <p className="text-gray-300 text-sm">{d.positioningRecommendation.avoid}</p>
                    </div>
                  </CollapsiblePanel>

                  {/* Panel 5: Funding & Geography */}
                  <CollapsiblePanel title="Funding & Geography" icon="" badge={d.fundingLandscape.investorInterestBarometer}>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                          <div style={{ padding: '8px 14px', borderRadius: 8, background: d.fundingLandscape.investorInterestBarometer === 'hot' ? '#ef444415' : d.fundingLandscape.investorInterestBarometer === 'warm' ? '#f59e0b15' : '#2a2a3e', border: `1px solid ${d.fundingLandscape.investorInterestBarometer === 'hot' ? '#ef444440' : d.fundingLandscape.investorInterestBarometer === 'warm' ? '#f59e0b40' : '#2a2a3e'}` }}>
                            <p style={{ color: d.fundingLandscape.investorInterestBarometer === 'hot' ? '#ef4444' : d.fundingLandscape.investorInterestBarometer === 'warm' ? '#f59e0b' : '#6b7280', fontWeight: 700, fontSize: 15 }}>
                              {d.fundingLandscape.investorInterestBarometer === 'hot' ? 'Hot' : d.fundingLandscape.investorInterestBarometer === 'warm' ? 'Warm' : 'Cold'} {d.fundingLandscape.investorInterestBarometer.charAt(0).toUpperCase() + d.fundingLandscape.investorInterestBarometer.slice(1)}
                            </p>
                            <p style={{ color: '#9ca3af', fontSize: 10, marginTop: 2 }}>Investor Interest</p>
                          </div>
                          <div className="space-y-1">{d.fundingLandscape.signals.map((sig, i) => <p key={i} className="text-gray-400 text-xs">• {sig}</p>)}</div>
                        </div>
                        {d.fundingLandscape.recentRounds.length > 0 && (
                          <div>
                            <p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, marginBottom: 6 }}>RECENT ROUNDS</p>
                            {d.fundingLandscape.recentRounds.map((r, i) => (
                              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #1e1e30', fontSize: 12 }}>
                                <span className="text-white">{r.company}</span>
                                <span style={{ color: '#22c55e' }}>{r.amount}</span>
                                <span style={{ color: '#6b7280' }}>{r.stage}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {d.geographicOpportunities.length > 0 && (
                        <div>
                          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide mb-3">Geographic Opportunities</p>
                          <div className="space-y-2">
                            {d.geographicOpportunities.map((geo, i) => (
                              <div key={i} style={{ padding: '8px 10px', borderRadius: 6, background: '#1e1e30' }}>
                                <p className="text-gray-300 text-xs">{geo}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CollapsiblePanel>

                  {/* Panel 6: Strategic Action Plan */}
                  {d.strategicAdvice.length > 0 && (
                    <CollapsiblePanel title="Strategic Action Plan" icon="" badge={`${d.strategicAdvice.length} actions`}>
                      {(['p0', 'p1', 'p2'] as const).map(priority => {
                        const actions = d.strategicAdvice.filter(a => a.priority === priority);
                        if (actions.length === 0) return null;
                        const pc = priority === 'p0'
                          ? { color: '#ef4444', bg: '#ef444415', label: 'P0 — Do This Now', border: '#ef444430' }
                          : priority === 'p1'
                          ? { color: '#f59e0b', bg: '#f59e0b10', label: 'P1 — Next 30 Days', border: '#f59e0b30' }
                          : { color: '#6b7280', bg: '#1e1e30', label: 'P2 — Next 90 Days', border: '#2a2a3e' };
                        return (
                          <div key={priority} style={{ marginBottom: 16 }}>
                            <p style={{ fontSize: 11, color: pc.color, fontWeight: 700, marginBottom: 8 }}>{pc.label}</p>
                            <div className="space-y-2">
                              {actions.map((a, i) => (
                                <div key={i} style={{ padding: '10px 12px', borderRadius: 8, background: pc.bg, border: `1px solid ${pc.border}` }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                                    <p className="text-white text-sm font-medium">{a.action}</p>
                                    <span style={{ fontSize: 10, color: '#6b7280', whiteSpace: 'nowrap', marginLeft: 12 }}>{a.timeframe}</span>
                                  </div>
                                  <p className="text-gray-400 text-xs">{a.rationale}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </CollapsiblePanel>
                  )}

                </div>
              );
            })()}
          </div>

          {/* ── TRENDS ── */}
          <div ref={trendsRef} style={{ marginTop: 48 }}>
            {sectionHeader('trends', 'trends', 'Market Trends')}
            {intel.trends.status === 'idle' && (
              <div className="card text-center py-12">
                <p className="mb-3"><TrendingUp size={36} style={{ color: '#7c6ff7' }} /></p>
                <p className="text-white font-semibold mb-1">Understand the market</p>
                <p className="text-gray-400 text-sm mb-4">Discover market size, growth rates, trends, and your timing assessment.</p>
                <button onClick={() => runSection('trends', 'trends')} className="btn-primary">Analyze Trends</button>
              </div>
            )}
            {intel.trends.status === 'loading' && <div className="card"><IntelLoadingCinematic sectionKey="trends" /></div>}
            {intel.trends.status === 'error' && <div className="card text-center py-8"><p className="text-red-400 mb-3">{intel.trends.error}</p><button onClick={() => runSection('trends', 'trends')} className="btn-secondary text-sm">Retry</button></div>}
            {intel.trends.status === 'done' && intel.trends.data && (() => {
              const d = intel.trends.data;
              const pctx = extractPipelineContext(pipelineData);
              const tc = d.timingAssessment.score >= 80 ? '#22c55e' : d.timingAssessment.score >= 60 ? '#f59e0b' : '#ef4444';
              const ic = d.investorInterest.level === 'high' ? '#22c55e' : d.investorInterest.level === 'medium' ? '#f59e0b' : '#ef4444';
              return (
                <div className="space-y-4 section-enter">
                  {/* Storytelling headline */}
                  <p style={{ fontSize: 20, fontWeight: 700, color: '#e5e7eb', lineHeight: 1.3 }}>
                    Is the market ready for {pctx.positioning ? 'your approach' : 'this idea'}?
                  </p>

                  {/* Hero: 4 KPI cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="card section-enter-d1 text-center"><p className="text-gray-500 text-xs mb-1">Market Size</p><p className="text-xl font-bold text-white">{d.marketSize.value ?? 'N/A'}</p>{d.marketSize.year && <p className="text-gray-500 text-xs mt-1">{d.marketSize.year}</p>}</div>
                    <div className="card section-enter-d1 text-center"><p className="text-gray-500 text-xs mb-1">Growth Rate</p><p className="text-xl font-bold text-green-400">{d.growthRate.value ?? 'N/A'}</p></div>
                    <div className="card section-enter-d1 text-center">
                      <p className="text-gray-500 text-xs mb-1">Market Timing</p>
                      <p className="text-xl font-bold" style={{ color: tc }}>{d.timingAssessment.verdict}</p>
                      <div style={{ height: 4, background: '#1e1e30', borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${d.timingAssessment.score}%`, background: tc, borderRadius: 2 }} />
                      </div>
                    </div>
                    <div className="card section-enter-d1 text-center"><p className="text-gray-500 text-xs mb-1">Investor Interest</p><p className="text-xl font-bold capitalize" style={{ color: ic }}>{d.investorInterest.level}</p><p className="text-gray-500 text-xs mt-1">{d.investorInterest.signals.length} signals</p></div>
                  </div>

                  {/* Cross-section insight chip */}
                  {intel.competitors.status === 'done' && intel.competitors.data && (
                    <InsightChip text={`Timing score ${d.timingAssessment.score}/100 with ${intel.competitors.data.competitors.length} competitors in market`} icon="" />
                  )}

                  {/* Panel 1: Trending Topics & Industry News */}
                  <CollapsiblePanel title="Trending Topics & Industry News" icon="" badge={`${d.trendingTopics.length} topics`}>
                    {d.trendingTopics.length > 0 && (
                      <div className="mb-4">
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          {d.trendingTopics.map((t, i) => (
                            <div key={i} style={{ padding: '8px 12px', borderRadius: 8, background: '#1e1e30', border: `1px solid ${t.momentum === 'rising' ? '#22c55e40' : t.momentum === 'declining' ? '#ef444440' : '#2a2a3e'}` }}>
                              <p className="text-white text-xs font-medium">{t.topic}</p>
                              <span style={{ fontSize: 10, color: t.momentum === 'rising' ? '#22c55e' : t.momentum === 'declining' ? '#ef4444' : '#9ca3af' }}>
                                {t.momentum === 'rising' ? '↑' : t.momentum === 'declining' ? '↓' : '→'} {t.momentum} · {t.source}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {d.industryNews.length > 0 && (
                      <div className="space-y-3">
                        {d.industryNews.slice(0, 6).map((n, i) => {
                          const displaySource = n.source && n.source !== 'Unknown' && n.source !== 'unknown'
                            ? n.source
                            : (() => { try { return new URL(n.url).hostname.replace(/^www\./, ''); } catch { return ''; } })();
                          return (
                            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', paddingBottom: 12, borderBottom: '1px solid #1e1e30' }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: n.sentiment === 'positive' ? '#22c55e' : n.sentiment === 'negative' ? '#ef4444' : '#6b7280' }}>{n.sentiment === 'positive' ? '+' : n.sentiment === 'negative' ? '-' : '~'}</span>
                              <div>
                                <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-white text-sm hover:text-brand-500 font-medium">{n.title}</a>
                                <p className="text-gray-400 text-xs mt-1">{n.summary.slice(0, 120)}{n.summary.length > 120 ? '…' : ''}</p>
                                <p className="text-gray-600 text-xs mt-1">{[displaySource, n.date].filter(Boolean).join(' · ')}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CollapsiblePanel>

                  {/* Panel 2: Opportunities & Threats */}
                  <CollapsiblePanel title="Opportunities & Threats" icon="">
                    <div className="grid sm:grid-cols-2 gap-4">
                      {d.founderOpportunities.length > 0 && (
                        <div>
                          <p style={{ fontSize: 11, color: '#22c55e', fontWeight: 700, marginBottom: 8 }}>OPPORTUNITIES</p>
                          {d.founderOpportunities.map((op, i) => (
                            <div key={i} style={{ padding: '8px 10px', borderRadius: 6, background: '#22c55e10', border: '1px solid #22c55e20', marginBottom: 8 }}>
                              <p className="text-white text-sm font-medium">{op.opportunity}</p>
                              <p className="text-gray-400 text-xs mt-1">{op.why}</p>
                              <p className="text-green-400 text-xs mt-1 font-medium">→ {op.actionableStep}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {d.threats.length > 0 && (
                        <div>
                          <p style={{ fontSize: 11, color: '#ef4444', fontWeight: 700, marginBottom: 8 }}>THREATS</p>
                          {d.threats.map((t, i) => (
                            <div key={i} style={{ padding: '8px 10px', borderRadius: 6, background: '#ef444410', border: '1px solid #ef444420', marginBottom: 8 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <p className="text-white text-sm font-medium">{t.threat.slice(0, 60)}</p>
                                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: t.severity === 'high' ? '#ef444420' : '#f59e0b20', color: t.severity === 'high' ? '#ef4444' : '#f59e0b' }}>{t.severity}</span>
                              </div>
                              <p className="text-gray-400 text-xs">{t.mitigation}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsiblePanel>

                  {/* Panel 3: Regulatory & Investor Signals */}
                  {(d.regulatoryRisks.length > 0 || d.investorInterest.signals.length > 0) && (
                    <CollapsiblePanel title="Regulatory & Investor Signals" icon="📜">
                      <div className="grid sm:grid-cols-2 gap-4">
                        {d.regulatoryRisks.length > 0 && (
                          <div>
                            <p style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, marginBottom: 8 }}>REGULATORY RADAR</p>
                            {d.regulatoryRisks.map((rr, i) => (
                              <div key={i} className="mb-2"><p className="text-gray-300 text-xs font-medium">{rr.risk.slice(0, 70)}</p><p className="text-gray-500 text-xs">{rr.description.slice(0, 100)}</p></div>
                            ))}
                          </div>
                        )}
                        {d.investorInterest.signals.length > 0 && (
                          <div>
                            <p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, marginBottom: 8 }}>INVESTOR SIGNALS</p>
                            {d.investorInterest.signals.map((sig, i) => <p key={i} className="text-gray-300 text-sm mb-1">• {sig}</p>)}
                            <p className="text-gray-500 text-xs mt-2">{d.timingAssessment.reasoning}</p>
                          </div>
                        )}
                      </div>
                    </CollapsiblePanel>
                  )}
                </div>
              );
            })()}
          </div>

          {/* ── SENTIMENT ── */}
          <div ref={sentimentRef} style={{ marginTop: 48 }}>
            {sectionHeader('sentiment', 'sentiment', 'Market Sentiment')}
            {intel.sentiment.status === 'idle' && (
              <div className="card text-center py-12">
                <p className="mb-3"><MessageSquare size={36} style={{ color: '#7c6ff7' }} /></p>
                <p className="text-white font-semibold mb-1">Hear what the market is saying</p>
                <p className="text-gray-400 text-sm mb-4">Analyze real pain points, desires, copywriting gold, and messaging framework.</p>
                <button onClick={() => runSection('sentiment', 'sentiment')} className="btn-primary">Analyze Sentiment</button>
              </div>
            )}
            {intel.sentiment.status === 'loading' && <div className="card"><IntelLoadingCinematic sectionKey="sentiment" /></div>}
            {intel.sentiment.status === 'error' && <div className="card text-center py-8"><p className="text-red-400 mb-3">{intel.sentiment.error}</p><button onClick={() => runSection('sentiment', 'sentiment')} className="btn-secondary text-sm">Retry</button></div>}
            {intel.sentiment.status === 'done' && intel.sentiment.data && (() => {
              const d = intel.sentiment.data;
              const pctx = extractPipelineContext(pipelineData);
              return (
                <div className="space-y-4 section-enter">
                  {/* Storytelling headline */}
                  <p style={{ fontSize: 20, fontWeight: 700, color: '#e5e7eb', lineHeight: 1.3 }}>
                    What {pctx.audience ?? 'the market'} really feel about {pctx.problem ?? 'this problem'}
                  </p>

                  {/* Hero: Customer Voice 3 cards + SentimentGauge + emotional triggers */}
                  <div className="card section-enter-d1" style={{ background: 'linear-gradient(135deg, #161627, #1a1a30)' }}>
                    <div className="grid sm:grid-cols-3 gap-4 mb-4">
                      {([
                        { label: 'Top Frustration', text: sanitizeCustomerVoice(d.customerVoice.topFrustration), color: '#ef4444', icon: '' },
                        { label: 'Top Desire', text: sanitizeCustomerVoice(d.customerVoice.topDesire), color: '#7c6ff7', icon: '' },
                        { label: 'Dream Outcome', text: sanitizeCustomerVoice(d.customerVoice.dreamOutcome), color: '#22c55e', icon: '' },
                      ] as const).map(({ label, text, color, icon }) => (
                        <div key={label} style={{ padding: '12px 14px', borderRadius: 8, background: `${color}10`, border: `1px solid ${color}30` }}>
                          <p style={{ fontSize: 11, color, fontWeight: 600, marginBottom: 6 }}>{icon} {label.toUpperCase()}</p>
                          <p className="text-gray-200 text-sm">{text}</p>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
                      <SentimentGauge score={d.sentimentScore} />
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <p className="text-white font-bold text-lg">{SENT_LABELS[d.overallSentiment] ?? d.overallSentiment}</p>
                        <p className="text-gray-400 text-sm mt-1">{d.positiveCount} positive · {d.negativeCount} negative signals</p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                          {d.emotionalTriggers.map((t, i) => (
                            <span key={i} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 12, background: '#7c6ff715', color: '#7c6ff7', border: '1px solid #7c6ff730' }}>{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Cross-section insight chip */}
                  {pctx.uniqueMechanism && (
                    <InsightChip text={`Pain alignment with your differentiator: "${pctx.uniqueMechanism}"`} icon="" />
                  )}

                  {/* Panel 1: Pain Points & Desires */}
                  <CollapsiblePanel title="Pain Points & Desires" icon="" badge={`${d.painPoints.length + d.desires.length} insights`}>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <p style={{ fontSize: 11, color: '#ef4444', fontWeight: 700, marginBottom: 8 }}>PAIN POINTS</p>
                        {d.painPoints.length > 0 ? d.painPoints.filter(p => sanitizeDesire(p.point) !== null).map((p, i) => (
                          <div key={i} style={{ padding: '8px 10px', borderRadius: 6, marginBottom: 6, background: p.severity === 'critical' ? '#ef444415' : '#1e1e30', border: `1px solid ${p.severity === 'critical' ? '#ef444430' : '#2a2a3e'}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                              <p className="text-gray-200 text-xs font-medium">{sanitizeLabel(p.point, 12) ?? p.point.slice(0, 60)}</p>
                              <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: p.severity === 'critical' ? '#ef444420' : p.severity === 'major' ? '#f59e0b20' : '#2a2a3e', color: p.severity === 'critical' ? '#ef4444' : p.severity === 'major' ? '#f59e0b' : '#6b7280' }}>{p.severity}</span>
                            </div>
                            <p className="text-green-400 text-xs">{sanitizeLabel(p.productImplication, 20) ?? p.productImplication}</p>
                          </div>
                        )) : <p className="text-gray-500 text-sm">No pain points detected</p>}
                      </div>
                      <div>
                        <p style={{ fontSize: 11, color: '#22c55e', fontWeight: 700, marginBottom: 8 }}>DESIRES & GOALS</p>
                        {d.desires.length > 0 ? d.desires.filter(des => sanitizeDesire(des.desire) !== null).map((des, i) => (
                          <div key={i} style={{ padding: '8px 10px', borderRadius: 6, marginBottom: 6, background: '#22c55e10', border: '1px solid #22c55e20' }}>
                            <p className="text-gray-200 text-xs font-medium mb-1">{sanitizeLabel(des.desire, 12) ?? des.desire.slice(0, 60)}</p>
                            <p className="text-green-400 text-xs">Feature idea: {sanitizeLabel(des.featureIdea, 15) ?? des.featureIdea}</p>
                          </div>
                        )) : <p className="text-gray-500 text-sm">No desires detected</p>}
                      </div>
                    </div>
                  </CollapsiblePanel>

                  {/* Panel 2: Copywriting Gold */}
                  <CollapsiblePanel title="Copywriting Gold" icon="" badge={`${d.copywritingGold.length} items`}>
                    <div className="space-y-2">
                      {d.copywritingGold.map((cg, i) => (
                        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', borderRadius: 8, background: '#1e1e30', border: '1px solid #2a2a3e', cursor: 'pointer' }} onClick={() => copyText(cg.text, i)}>
                          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, background: cg.type === 'headline' ? '#7c6ff720' : cg.type === 'cta' ? '#00d4ff20' : '#22c55e20', color: cg.type === 'headline' ? '#7c6ff7' : cg.type === 'cta' ? '#00d4ff' : '#22c55e', whiteSpace: 'nowrap', fontWeight: 600 }}>{cg.type}</span>
                          <p className="text-gray-200 text-sm flex-1">{cg.text}</p>
                          <span style={{ fontSize: 11, color: copiedIdx === i ? '#22c55e' : '#4b5563', whiteSpace: 'nowrap' }}>{copiedIdx === i ? 'Copied' : 'Copy'}</span>
                        </div>
                      ))}
                    </div>
                  </CollapsiblePanel>

                  {/* Panel 3: Product Roadmap Hints */}
                  {d.productRoadmapHints.length > 0 && (
                    <CollapsiblePanel title="Product Roadmap Hints" icon="" badge={`${d.productRoadmapHints.length} features`}>
                      {d.productRoadmapHints.map((hint, i) => (
                        <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 10 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap', background: hint.priority === 'p0' ? '#ef444420' : hint.priority === 'p1' ? '#f59e0b20' : '#2a2a3e', color: hint.priority === 'p0' ? '#ef4444' : hint.priority === 'p1' ? '#f59e0b' : '#6b7280' }}>{hint.priority.toUpperCase()}</span>
                          <div>
                            <p className="text-gray-200 text-sm">{hint.feature}</p>
                            <p className="text-gray-500 text-xs mt-0.5">Based on: {hint.basedOnSentiment.slice(0, 60)}</p>
                          </div>
                        </div>
                      ))}
                    </CollapsiblePanel>
                  )}

                  {/* Panel 4: Messaging Framework (PAS) */}
                  <CollapsiblePanel title="Messaging Framework" icon="">
                    <div className="space-y-3">
                      {([
                        { label: 'OPEN WITH PAIN', text: d.messagingFramework.openWithPain, color: '#ef4444' },
                        { label: 'AGITATE', text: d.messagingFramework.agitate, color: '#f97316' },
                        { label: 'SOLUTION', text: d.messagingFramework.solution, color: '#7c6ff7' },
                        { label: 'CTA', text: d.messagingFramework.cta, color: '#22c55e' },
                      ] as const).map(({ label, text, color }, i) => (
                        <div key={label} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }} onClick={() => copyText(text, 100 + i)}>
                          <span style={{ fontSize: 10, fontWeight: 600, color, background: `${color}20`, padding: '2px 8px', borderRadius: 4, whiteSpace: 'nowrap', marginTop: 2 }}>{label}</span>
                          <p className="text-gray-200 text-sm flex-1">{text}</p>
                          <span style={{ fontSize: 11, color: copiedIdx === 100 + i ? '#22c55e' : '#4b5563' }}>{copiedIdx === 100 + i ? 'Copied' : 'Copy'}</span>
                        </div>
                      ))}
                    </div>
                  </CollapsiblePanel>
                </div>
              );
            })()}
          </div>

          {/* ── AI SYNTHESIS (CHANGE 6) — between intel sections and Beta & Feedback ── */}
          <SynthesisCard
            startupId={startupId!}
            pipeline={pipelineData}
            intel={intel as IntelSectionsForSynthesis}
            doneCount={doneCount}
          />

          {/* ── BETA & FEEDBACK ── */}
          <div ref={feedbackRef} style={{ marginTop: 48, paddingBottom: 64 }}>
            <h2 className="flex items-center gap-2 text-xl font-bold text-white mb-4"><Users size={18} className="text-brand-500" /> Beta & Feedback</h2>
            <div className="space-y-4">
              {/* CHANGE 9: unlock flow — show recruitment panel when no beta users */}
              {(betaUsers?.length ?? 0) === 0 ? (
                <BetaRecruitmentPanel startupId={startupId!} pipeline={pipelineData} />
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  <div className="card"><p className="text-gray-500 text-xs mb-1">Beta Users</p><p className="text-2xl font-bold text-white">{betaUsers.length}</p></div>
                  <div className="card"><p className="text-gray-500 text-xs mb-1">Feedback Responses</p><p className="text-2xl font-bold text-white">{feedbackAnalytics.totalResponses}</p></div>
                  <div className="card"><p className="text-gray-500 text-xs mb-1">NPS Score</p><p className="text-2xl font-bold" style={{ color: npsColor }}>{feedbackAnalytics.totalResponses > 0 ? `+${metrics.betaNPS}` : '—'}</p></div>
                </div>
              )}
              {topFeatureRequests.length > 0 && (
                <div className="card">
                  <h3 className="text-white font-semibold mb-3">All Feature Requests</h3>
                  {topFeatureRequests.map((fr, i) => (
                    <div key={fr.name} className="flex items-center justify-between text-sm border-b border-[#2a2a3e] pb-2 mb-2">
                      <span className="text-gray-300"><span className="text-gray-600 mr-2">#{i + 1}</span>{fr.name}</span>
                      <span className="text-brand-500 text-xs">{fr.count} req</span>
                    </div>
                  ))}
                </div>
              )}
              {/* P2-22: Early Adopter Persona card */}
              {(() => {
                const persona = (pipelineData?.positioning as any)?.aiOutput?.earlyAdopterPersona;
                if (!persona?.name) return null;
                return (
                  <div className="card">
                    <h3 className="text-white font-semibold mb-3">Early Adopter Persona</h3>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <p style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{persona.name}</p>
                        <p style={{ fontSize: 12, color: '#9ca3af' }}>{persona.age} · {persona.job}</p>
                        {persona.frustration && (
                          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: '#ef444408', border: '1px solid #ef444420' }}>
                            <p style={{ fontSize: 10, color: '#ef4444', fontWeight: 700, marginBottom: 4 }}>FRUSTRATION</p>
                            <p style={{ fontSize: 13, color: '#d1d5db' }}>{persona.frustration}</p>
                          </div>
                        )}
                        {persona.goal && (
                          <div style={{ marginTop: 8, padding: '10px 14px', borderRadius: 8, background: '#22c55e08', border: '1px solid #22c55e20' }}>
                            <p style={{ fontSize: 10, color: '#22c55e', fontWeight: 700, marginBottom: 4 }}>GOAL</p>
                            <p style={{ fontSize: 13, color: '#d1d5db' }}>{persona.goal}</p>
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        {persona.whereToFind && (
                          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#7c6ff708', border: '1px solid #7c6ff720', marginBottom: 8 }}>
                            <p style={{ fontSize: 10, color: '#7c6ff7', fontWeight: 700, marginBottom: 4 }}>WHERE TO FIND THEM</p>
                            <p style={{ fontSize: 13, color: '#d1d5db' }}>{persona.whereToFind}</p>
                          </div>
                        )}
                        {persona.quote && (
                          <div style={{ padding: '10px 14px', borderRadius: 8, background: '#1e1e30' }}>
                            <p style={{ fontSize: 13, color: '#d1d5db', fontStyle: 'italic' }}>"{persona.quote}"</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* CHANGE 7: FounderSummaryEditor replaces raw textarea */}
              <FounderSummaryEditor
                value={summary}
                onChange={setSummary}
                onSave={handleSaveSummary}
                isSaving={isSavingSummary}
                pipeline={pipelineData}
                startupId={startupId!}
              />
            </div>
          </div>

        </div>{/* end main content */}
      </div>{/* end flex layout */}
    </div>

    {/* UX 7: Credit confirmation with "Don't ask again" + insufficient credit link */}
    {creditConfirm && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)' }}
        onClick={() => setCreditConfirm(null)}>
        <div style={{ background: '#161627', borderRadius: 16, padding: '28px 32px', maxWidth: 380, width: '100%', border: '1px solid #2a2a3e' }}
          onClick={e => e.stopPropagation()}>
          <p style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
            {creditConfirm.force ? 'Re-analyze' : 'Run'} {creditConfirm.label}?
          </p>
          <p style={{ fontSize: 13, color: '#8888aa', marginBottom: 12 }}>
            This will use <span style={{ color: '#f59e0b', fontWeight: 700 }}>{creditConfirm.cost} credits</span>.
            You have <span style={{ color: credits >= creditConfirm.cost ? '#22c55e' : '#ef4444', fontWeight: 700 }}>{credits} credits</span>.
          </p>
          {credits < creditConfirm.cost && (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12 }}>
              <span style={{ color: '#ef4444' }}>Insufficient credits. </span>
              <Link to="/credits" onClick={() => setCreditConfirm(null)} style={{ color: '#f97316', textDecoration: 'underline' }}>
                Buy more →
              </Link>
            </div>
          )}
          {credits >= creditConfirm.cost && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
              <input
                type="checkbox"
                onChange={e => { if (e.target.checked) localStorage.setItem('sf_skip_credit_confirm', '1'); }}
                style={{ accentColor: '#7c6ff7' }}
              />
              <span style={{ fontSize: 12, color: '#6b7280' }}>Don't ask again</span>
            </label>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setCreditConfirm(null)} className="btn-secondary" style={{ flex: 1 }}>Cancel</button>
            <button
              onClick={() => { const cc = creditConfirm; setCreditConfirm(null); runSectionDirect(cc.key, cc.endpoint, cc.force); }}
              disabled={credits < creditConfirm.cost}
              className="btn-primary"
              style={{ flex: 1, opacity: credits < creditConfirm.cost ? 0.5 : 1 }}
            >
              {creditConfirm.force ? 'Re-analyze' : 'Run Analysis'} ({creditConfirm.cost} credits)
            </button>
          </div>
        </div>
      </div>
    )}

    {/* FIX: BUG 3 - Run All confirmation modal */}
    {/* FIX: BUG 3 - proper Run All confirmation modal with per-module breakdown */}
    {runAllConfirm && (
      <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)' }}
        onClick={() => setRunAllConfirm(null)}>
        <div style={{ background: '#161627', borderRadius: 16, padding: '28px 32px', maxWidth: 420, width: '100%', border: '1px solid #2a2a3e' }}
          onClick={e => e.stopPropagation()}>
          <p style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Run All Intelligence Modules?</p>
          <div style={{ marginBottom: 16 }}>
            {INTEL_TASKS.map(t => {
              const done = intel[t.key].status === 'done';
              return (
                <div key={t.key} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                  <span style={{ color: done ? '#6b7280' : '#f0f0ff' }}>{done ? '✓' : '○'} {t.label}</span>
                  <span style={{ color: done ? '#374151' : '#f59e0b', fontWeight: 600 }}>
                    {done ? 'cached' : `${t.cost} credits`}
                  </span>
                </div>
              );
            })}
          </div>
          <div style={{ borderTop: '1px solid #2a2a3e', paddingTop: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700 }}>
              <span style={{ color: '#f0f0ff' }}>Total cost</span>
              <span style={{ color: '#f59e0b' }}>{runAllConfirm.totalCost} credits</span>
            </div>
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Your balance: <span style={{ color: credits >= runAllConfirm.totalCost ? '#22c55e' : '#ef4444', fontWeight: 700 }}>{credits} credits</span>
              {credits < runAllConfirm.totalCost && <span style={{ color: '#ef4444' }}> — insufficient</span>}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setRunAllConfirm(null)} className="btn-secondary" style={{ flex: 1 }}>Cancel</button>
            <button
              onClick={() => runAllSequential()}
              disabled={credits < runAllConfirm.totalCost}
              className="btn-primary"
              style={{ flex: 1, opacity: credits < runAllConfirm.totalCost ? 0.5 : 1 }}
            >
              Run All ({runAllConfirm.totalCost} credits)
            </button>
          </div>
        </div>
      </div>
    )}

    {creditModal && (
      <InsufficientCreditsModal
        action={creditModal.action}
        required={creditModal.required}
        available={creditModal.available}
        onClose={() => { setCreditModal(null); fetchBalance(); }}
      />
    )}

    {/* Milestone Celebration */}
    <MilestoneCelebration milestone={activeMilestone} onClose={() => setActiveMilestone(null)} />
    </>
  );
}
